export interface VideoResource {
  title: string;
  url: string;
  channel: string;
  note?: string;
}

export interface ProcedureData {
  id: string;
  title: string;
  category: string;
  categoryId: string;
  overview: {
    description: string;
    indications: string[];
    contraindications: string[];
  };
  supplies: string[];
  setup: string[];
  steps: string[];
  videos: VideoResource[];
  butterflyLinks?: { label: string; url: string }[];
  aftercare: string[];
  pearls: string[];
}

export const categories = [
  { id: 'msk', label: 'MSK Injections', icon: 'syringe' },
  { id: 'derm', label: 'Dermatology', icon: 'skin' },
  { id: 'womens', label: "Women's Health", icon: 'health' },
  { id: 'audiology', label: 'Audiology', icon: 'ear' },
  { id: 'pocus', label: 'POCUS', icon: 'ultrasound' },
];

export const procedures: ProcedureData[] = [
  // ─────────────────────────── MSK INJECTIONS ───────────────────────────
  {
    id: 'knee-injection',
    title: 'Knee Injection / Aspiration',
    category: 'MSK Injections',
    categoryId: 'msk',
    overview: {
      description:
        'Intra-articular injection of corticosteroid ± anesthetic, or aspiration of joint fluid from the knee. Commonly performed for osteoarthritis, effusions, gout, and inflammatory arthropathy. One of the most frequently performed outpatient procedures in family medicine.',
      indications: [
        'Knee osteoarthritis with pain or effusion',
        'Acute inflammatory arthritis (gout, pseudogout, reactive)',
        'Diagnostic aspiration for joint fluid analysis',
        'Post-traumatic effusion or hemarthrosis',
        'Prepatellar or pes anserine bursitis (adjacent)',
      ],
      contraindications: [
        'Overlying skin infection or cellulitis',
        'Suspected septic joint (aspirate first if uncertain)',
        'Prosthetic joint (refer to orthopedics)',
        'Anticoagulation is a relative contraindication — review carefully',
        'Recent injection at same site within 3 months',
        'Allergy to injectate components',
      ],
    },
    supplies: [
      '18–22g needle (18g preferred for aspiration, 22g for injection only)',
      '10–20 mL syringe (for aspiration); 3–5 mL syringe (for injection)',
      'Corticosteroid: triamcinolone 40 mg/mL or methylprednisolone 40 mg/mL',
      'Lidocaine 1% (3–5 mL) or bupivacaine 0.25%',
      'Povidone-iodine or chlorhexidine prep',
      'Sterile gloves',
      'Fenestrated drape (optional)',
      'Specimen tubes if sending synovial fluid (purple-top, green-top, culture)',
      'Bandage',
    ],
    setup: [
      'Patient supine with knee fully extended (preferred) or slightly flexed at 15–20°',
      'Medial approach: patient supine, find the soft spot medial to patella midpoint',
      'Lateral approach: palpate lateral joint line just posterior to patella — often easier for large effusions',
      'Mark entry point with pen cap or thumbnail indent before prepping',
      'Set up aspiration syringe first if planning to aspirate, then switch to injection syringe',
      'A "two-syringe technique" lets you aspirate then inject without withdrawing the needle',
      'Ultrasound guidance increases accuracy significantly — use it if available for diagnostic aspiration',
    ],
    steps: [
      'Position patient supine, knee extended or slightly flexed',
      'Identify and mark entry point (medial or lateral to midpatella)',
      'Prep skin with antiseptic x2, allow to dry',
      'Don sterile gloves; consider sterile drape',
      'Optional: raise skin wheal with 1 mL lidocaine using 25g needle',
      'Advance 18–22g needle at marked site, angled slightly posterior and toward joint center',
      'For aspiration: attach large syringe and aspirate; fluid should flow freely',
      'For effusion: drain completely before injecting — use two-syringe technique',
      'Confirm intra-articular placement: fluid returns freely, no resistance to injection',
      'Inject corticosteroid ± anesthetic; patient should feel no significant resistance',
      'Withdraw needle, apply gentle pressure, bandage',
      'Have patient flex and extend knee several times to distribute medication',
    ],
    videos: [
      { title: 'Anterior Knee Injection - Elite Sports Medicine', url: 'https://www.youtube.com/watch?v=oM4_uFQEDrs', channel: 'Elite Sports Medicine', note: 'Clear landmark technique, medial and lateral approach demonstration' },
      { title: 'IPASS 5: Landmark-Based Knee Injections', url: 'https://www.youtube.com/watch?v=quTcrGDlZyQ', channel: 'American Family Physician', note: 'FM-specific landmark-based knee injection overview' },
    ],
    aftercare: [
      'Rest the joint for 24–48 hours; avoid high-impact activity for 1 week',
      'Ice the knee 15–20 minutes several times daily for 48 hours',
      'Expected: mild soreness or "steroid flare" for 24–48 hours after injection — reassure patient',
      'Full effect of corticosteroid typically felt within 3–7 days',
      'Return precautions: increasing warmth, redness, swelling, or fever → rule out septic joint',
      'Counsel on frequency: typically limit to 3–4 injections per year to minimize cartilage effects',
      'Document: site, medication, dose, lot number, patient tolerance, fluid characteristics if aspirated',
    ],
    pearls: [
      '🎯 Medial approach: imagine sliding the needle under the patella — aim slightly posterior',
      '⚠️ "Post-injection flare" is real — warn patients before they leave so they don\'t call after hours',
      '💡 Milk the joint before injecting: if aspirating, get all fluid out first for best steroid contact',
      '🔍 Always inspect aspirated fluid: yellow-clear = OA, cloudy = inflammatory/infectious, bloody = trauma/hemarthrosis',
      '📐 If you meet resistance during injection, you\'re likely in periarticular tissue — reposition slightly',
      '🚫 Avoid injecting into clearly septic joints — aspirate, send cultures, do not inject steroids',
      '✅ Ultrasound guidance: strongly consider for small effusions or patients with obesity — dramatically improves accuracy',
      '⏱️ Common resident mistake: injecting too superficially (into fat pad) — the needle needs to clear the patella and enter the joint space',
    ],
  },

  {
    id: 'shoulder-injection',
    title: 'Shoulder Injection',
    category: 'MSK Injections',
    categoryId: 'msk',
    overview: {
      description:
        'Corticosteroid injection into the subacromial bursa or glenohumeral joint for pain relief and functional improvement. Subacromial injections are the most common and are appropriate for rotator cuff tendinopathy and subacromial bursitis. Glenohumeral injections target inflammatory arthritis or adhesive capsulitis.',
      indications: [
        'Subacromial bursitis',
        'Rotator cuff tendinopathy (supraspinatus impingement)',
        'Adhesive capsulitis (frozen shoulder)',
        'Glenohumeral osteoarthritis',
        'Rotator cuff partial tear with preserved function',
      ],
      contraindications: [
        'Overlying skin infection or cellulitis',
        'Complete rotator cuff tear (relative — refer)',
        'Suspected septic joint',
        'Known allergy to injectate',
        'Prosthetic shoulder joint',
        '>3–4 injections per year (cumulative tendon weakening)',
      ],
    },
    supplies: [
      '22–25g needle, 1.5 inch',
      '5–10 mL syringe',
      'Triamcinolone 40 mg/mL (1 mL) or methylprednisolone 40 mg/mL',
      'Lidocaine 1% (3–4 mL)',
      'Chlorhexidine or betadine prep',
      'Sterile gloves',
      'Bandage',
    ],
    setup: [
      'Patient seated on exam table, arm relaxed at side (not abducted)',
      'Posterior approach: sit behind the patient; palpate the posterior soft spot 1 cm inferior and medial to the posterolateral acromion corner',
      'Lateral approach: identify midpoint of acromion lateral edge; enter 1–2 cm inferior toward contralateral nipple',
      'Anterior approach: patient supine, arm in slight ER; enter lateral to coracoid process — less common',
      'Ask patient to let arm "hang like a wet noodle" — relaxation opens the subacromial space',
      'Landmark the "dimple" lateral to acromion — this is your target',
    ],
    steps: [
      'Seat patient with arm relaxed at side',
      'Identify and mark entry point (posterior or lateral approach)',
      'Prep skin with antiseptic, allow to dry',
      'Don sterile gloves',
      'Insert 22g needle at marked site, aiming toward the anterior acromion (posterior approach) or inferior acromion (lateral approach)',
      'Advance until minimal resistance is met — you\'re in the bursa',
      'Aspirate: minimal return expected (bursal fluid is sparse unless bursitis present)',
      'Inject slowly: should feel minimal resistance; resistance = likely in tendon — reposition',
      'Withdraw, apply pressure, bandage',
      'Have patient perform gentle pendulum exercises immediately',
    ],
    videos: [
      { title: 'Injection Technique into the Subacromial Space', url: 'https://www.youtube.com/watch?v=xd3osL-s5YQ', channel: 'Medical Skills Courses', note: 'Step-by-step subacromial injection with anatomy review' },
      { title: 'How to do a Subacromial Shoulder Injection', url: 'https://www.youtube.com/watch?v=widybt2VNik', channel: 'UCSF Orthopaedic Surgery', note: 'Academic medical center technique demonstration' },
    ],
    aftercare: [
      'Arm sling is generally NOT needed — discourage immobilization',
      'Gentle range-of-motion exercises (pendulum) starting same day',
      'Ice 15–20 min several times daily for 48 hours',
      'Avoid heavy lifting or overhead work for 1 week',
      'Expect onset of benefit within 3–7 days',
      'Physical therapy referral if not improved in 4–6 weeks',
      'Return precautions: increasing pain, fever, warmth → rule out joint infection',
    ],
    pearls: [
      '🎯 Posterior approach is easiest for most residents — aim toward the coracoid process (anterior shoulder)',
      '💡 If resistance met during injection: you\'re likely in tendon. Pull back 2–3 mm and retry',
      '⚠️ Adhesive capsulitis: glenohumeral injection (anterior approach) more appropriate than subacromial',
      '🔍 Always assess active vs. passive ROM pre-injection to document baseline',
      '📐 Subacromial space is only about 1–2 cm wide — needle depth matters',
      '✅ "Wet noodle arm" reminder: if the patient is tensing their deltoid, the subacromial space narrows significantly',
      '⏱️ Common mistake: going too deep and hitting the humeral head — you\'ll feel resistance immediately',
      '🚫 Don\'t inject if you suspect complete rotator cuff tear — get imaging first',
    ],
  },

  {
    id: 'trochanteric-bursa',
    title: 'Trochanteric Bursa Injection',
    category: 'MSK Injections',
    categoryId: 'msk',
    overview: {
      description:
        'Injection of corticosteroid into the trochanteric bursa for greater trochanteric pain syndrome (GTPS), formerly called "trochanteric bursitis." Most cases are actually gluteal tendinopathy or bursitis of the trochanteric bursa. Highly effective when conservative measures fail.',
      indications: [
        'Greater trochanteric pain syndrome (lateral hip pain, tender over GT)',
        'Trochanteric bursitis confirmed on exam',
        'Failed conservative management (NSAIDs, PT, rest)',
        'Iliotibial band syndrome with trochanteric involvement',
      ],
      contraindications: [
        'Overlying skin infection',
        'Suspected hip joint pathology (pain with internal rotation → hip joint, not bursa)',
        'Allergy to injectate',
        'Coagulopathy (relative)',
      ],
    },
    supplies: [
      '22g needle, 3.5 inch (spinal needle may be needed for larger patients)',
      '5–10 mL syringe',
      'Triamcinolone 40 mg/mL (1–2 mL)',
      'Lidocaine 1% (3–4 mL)',
      'Antiseptic prep',
      'Sterile gloves',
      'Bandage',
    ],
    setup: [
      'Patient lateral decubent (side-lying) with affected side up',
      'Hip in neutral position (slight flexion acceptable)',
      'Palpate the greater trochanter — the most prominent bony landmark on the lateral hip',
      'Identify the point of maximal tenderness — typically the posterior facet of the greater trochanter',
      'Mark the entry point before prepping',
      'For larger patients, a 3.5-inch spinal needle may be needed to reach the bursa',
    ],
    steps: [
      'Position patient in lateral decubitus, affected side up',
      'Palpate and mark point of maximal tenderness over greater trochanter',
      'Prep skin, don sterile gloves',
      'Insert needle perpendicular to the skin, advancing until bony contact with the greater trochanter',
      'Pull back 2–3 mm from bone — you are now in the periosteal/bursal space',
      'Aspirate: minimal fluid return expected in most cases',
      'Inject slowly; slight resistance is normal (bursa is small)',
      'Fan injection technique: inject, withdraw slightly, redirect 2–3 times to spread medication',
      'Withdraw needle, apply pressure, bandage',
    ],
    videos: [
      { title: 'Injection Technique of the Trochanteric Bursa for Bursitis', url: 'https://www.youtube.com/watch?v=rk41GpRaiBk', channel: 'Medical Skills Courses', note: 'Clear lateral decubitus positioning and landmark technique' },
      { title: 'Greater Trochanteric Bursa Injection', url: 'https://www.youtube.com/watch?v=M9qnZ4yXPxk', channel: 'Dr Kosh', note: 'Concise overview with key anatomical landmarks' },
    ],
    aftercare: [
      'Avoid lying on injected side for 24 hours',
      'Ice to area 15–20 min twice daily for 48 hours',
      'Gradual return to walking and activity over 1 week',
      'Physical therapy (hip abductor strengthening) after injection for lasting benefit',
      'Return precautions: worsening pain, fever, increasing swelling',
      'Counsel: injection treats bursitis; underlying gluteal tendinopathy requires PT for long-term resolution',
    ],
    pearls: [
      '🎯 Touch the bone first, then pull back — this ensures bursal space placement',
      '💡 Fan technique improves distribution of medication in the bursa',
      '⚠️ Distinguish from hip OA: hip OA pain is groin-based, painful with IR. Bursal pain is lateral, no IR restriction',
      '📐 Larger patients: standard 1.5" needles won\'t reach the bursa — always use 3–3.5" in patients with larger habitus',
      '✅ Consider ultrasound guidance if injection fails — may identify gluteal tendon tear instead',
      '⏱️ Common mistake: too anterior placement (IT band insertion area) — stay over the posterior facet of the GT',
      '🔍 Reassess diagnosis if injection provides no relief — consider hip fracture, avascular necrosis, or lumbar radiculopathy',
    ],
  },

  {
    id: 'trigger-finger',
    title: 'Trigger Finger Injection',
    category: 'MSK Injections',
    categoryId: 'msk',
    overview: {
      description:
        'Corticosteroid injection into the flexor tendon sheath at the A1 pulley level for stenosing tenosynovitis (trigger finger/thumb). Highly effective for mild-to-moderate cases. Success rate ~50–80% with single injection; may repeat once if partial response.',
      indications: [
        'Stenosing tenosynovitis (trigger finger or thumb)',
        'Grade I–III triggering (pain, clicking, locking with active correction)',
        'Failed conservative management (splinting, NSAIDs)',
        'Patient preference to avoid surgery',
      ],
      contraindications: [
        'Grade IV triggering with fixed flexion contracture (refer to surgery)',
        'Overlying skin infection',
        'Known tendon rupture',
        'Allergy to injectate',
        '>2 prior injections at same site',
      ],
    },
    supplies: [
      '25–27g needle, 0.5–1 inch',
      '2–3 mL syringe',
      'Triamcinolone 10–20 mg (0.5–1 mL of 40 mg/mL) OR betamethasone',
      'Lidocaine 1% (0.5–1 mL) — optional',
      'Betadine or chlorhexidine prep',
      'Sterile gloves',
      'Bandage',
    ],
    setup: [
      'Patient seated with hand palm-up, fingers extended on a flat surface',
      'Palpate the A1 pulley: located at the palmar crease at the MCP joint',
      'The palpable nodule (if present) is the tendon bunching proximal to the pulley',
      'Mark entry site at the proximal flexor crease (MCP level)',
      'Clean and prep the palm carefully — this is not a sterile field by default, so thorough antiseptic prep is essential',
      'Ring or long finger most commonly affected; thumb second most common',
    ],
    steps: [
      'Position hand palm-up on flat surface',
      'Palpate A1 pulley at MCP flexor crease; mark entry point',
      'Prep skin thoroughly with antiseptic',
      'Insert 25g needle at 45° angle, bevel up, at the proximal MCP crease',
      'Advance 5–8 mm into the tendon sheath',
      'Test placement: have patient gently flex and extend — needle should move with tendon if intratendinous (reposition!)',
      'If needle is peritendinous/intrasheath: no movement with finger flexion',
      'Inject slowly: should feel minimal resistance; if tight, redirect slightly',
      'Withdraw needle, apply pressure',
      'Have patient flex/extend finger to distribute medication',
    ],
    videos: [
      { title: 'How to Perform a Trigger Finger (A1 Pulley) Injection', url: 'https://www.youtube.com/watch?v=0VSxtk1oQ2s', channel: 'Dr Thomas McClellan', note: 'Detailed A1 pulley technique with needle placement tips' },
      { title: 'Trigger Finger Injection: Step-By-Step Instructions', url: 'https://www.youtube.com/watch?v=2Pwgglg_m6M', channel: 'RheumTutor', note: 'Step-by-step with needle dance test demonstration' },
    ],
    aftercare: [
      'Buddy tape or gentle splinting for 24–48 hours optional',
      'Expect improvement in triggering within 1–2 weeks',
      'Ice the palm 10–15 minutes TID for 48 hours',
      'Avoid heavy gripping or repetitive use for 1 week',
      'If no improvement in 3–4 weeks, may repeat once',
      'Return precautions: signs of infection (warmth, spreading redness, fever)',
      'Counsel on surgical option if 2 injections fail to provide durable relief',
    ],
    pearls: [
      '🎯 The "needle dance" test: ask patient to flex/extend finger after inserting needle — movement = intratendinous (bad). No movement = peritendinous sheath (good)',
      '💡 A2 pulley injections are too distal — always target the A1 pulley at the MCP crease',
      '⚠️ Thumb trigger: use a separate A1 pulley location (MCP crease) — anatomy slightly different',
      '📐 Don\'t go too deep — you only need to penetrate the sheath, not the tendon itself',
      '✅ Consider mixing betamethasone (lower particle size, less tendon toxicity) over triamcinolone for finger tendons',
      '⏱️ Common mistake: injecting at the DIP or PIP level instead of the MCP/A1 pulley level',
      '🔍 Diabetic patients: steroid injections may transiently raise blood glucose — warn them, especially Type 1 DM',
    ],
  },

  {
    id: 'lateral-epicondyle',
    title: 'Lateral Epicondyle Injection',
    category: 'MSK Injections',
    categoryId: 'msk',
    overview: {
      description:
        'Peritendinous corticosteroid injection for lateral epicondylitis (tennis elbow), targeting the extensor carpi radialis brevis origin at the lateral epicondyle. Evidence shows short-term benefit (pain relief at 6 weeks) but may have higher recurrence than watchful waiting long-term. Use as part of a multimodal approach.',
      indications: [
        'Lateral epicondylitis (tennis elbow) resistant to conservative therapy',
        'ECRB tendinopathy with focal tenderness at lateral epicondyle',
        'Failed physical therapy and NSAIDs ≥4–6 weeks',
        'Need for rapid short-term pain relief (e.g., occupational demands)',
      ],
      contraindications: [
        'Overlying skin infection',
        'Suspected radial tunnel syndrome (pain 4–5 cm distal to epicondyle)',
        'Allergy to injectate',
        '>3 injections at same site (tendon weakening, fat pad atrophy)',
      ],
    },
    supplies: [
      '25–27g needle, 1 inch',
      '2–3 mL syringe',
      'Triamcinolone 20–40 mg (0.5–1 mL)',
      'Lidocaine 1% (1–2 mL) — optional',
      'Antiseptic prep',
      'Sterile gloves',
      'Bandage',
    ],
    setup: [
      'Patient seated with elbow flexed 90° and resting on table, palm down',
      'Palpate the lateral epicondyle — the bony prominence at the lateral distal humerus',
      'Find the point of maximal tenderness just anterior and distal to the epicondyle (ECRB origin)',
      'Mark the entry site',
      'Consider using the "peppering" technique for resistant cases',
    ],
    steps: [
      'Seat patient, elbow flexed 90°, forearm pronated on table',
      'Palpate and mark point of maximal tenderness at lateral epicondyle/ECRB origin',
      'Prep skin; don sterile gloves',
      'Insert 25g needle at marked site, perpendicular to skin',
      'Advance until contact with bone (periosteal level)',
      'Pull back 1–2 mm — peritendinous placement at tendon origin',
      'Inject: expect slight resistance (peritendinous); high resistance = intratendinous (reposition)',
      'Peppering technique: partially withdraw, redirect, re-inject in fan pattern ×3–5 passes',
      'Withdraw, apply pressure, bandage',
    ],
    videos: [
      { title: 'Injection Technique for Lateral Epicondylalgia', url: 'https://www.youtube.com/watch?v=8BYcYnN-H1s', channel: 'Medical Skills Courses', note: 'Landmark technique with peppering demonstration' },
      { title: 'Tennis Elbow Lateral Epicondylitis Injection', url: 'https://www.youtube.com/watch?v=92FXZlXZaf0', channel: 'nabil ebraheim', note: 'Anatomy review and injection technique walkthrough' },
    ],
    aftercare: [
      'Rest elbow from aggravating activities for 2–4 weeks',
      'Ice to lateral elbow TID for 48 hours',
      'Counterforce brace (epicondyle strap) can be used during activity after injection',
      'Physical therapy referral for eccentric strengthening — key for lasting improvement',
      'Expect symptom relief within 1–2 weeks; may take up to 4 weeks for full effect',
      'Return precautions: increasing swelling, warmth, or fever',
      'Counsel on long-term: steroid injection is a bridge; rehab is the treatment',
    ],
    pearls: [
      '🎯 Touch the bone, then back off 1–2 mm — avoid intratendinous injection',
      '💡 Peppering technique (multiple small passes in fan pattern) provides better distribution at the tendon origin',
      '⚠️ Long-term evidence: cortisone may not outperform watchful waiting at 1 year — set realistic expectations',
      '📐 Subcutaneous fat atrophy and skin hypopigmentation can occur — stay peritendinous, not subcutaneous',
      '✅ Platelet-rich plasma (PRP) is emerging as alternative — refer if repeated injections fail',
      '⏱️ Common mistake: injecting the medial epicondyle instead (different anatomy) — palpate carefully',
      '🔍 Radial tunnel syndrome mimics lateral epicondylitis but is more distal — don\'t inject here',
    ],
  },

  {
    id: 'plantar-fascia',
    title: 'Plantar Fascia Injection',
    category: 'MSK Injections',
    categoryId: 'msk',
    overview: {
      description:
        'Corticosteroid injection for plantar fasciitis targeting the fascia origin at the medial calcaneal tubercle. Most effective when conservative measures (stretching, orthotics, night splints) have failed. Risk of plantar fascia rupture and fat pad atrophy with repeated injections.',
      indications: [
        'Plantar fasciitis resistant to conservative treatment ≥4–6 weeks',
        'Focal tenderness at medial calcaneal tubercle',
        'Significant functional limitation',
        'Failed stretching protocol, orthotics, and NSAIDs',
      ],
      contraindications: [
        'Overlying skin infection',
        '>3 prior injections (rupture risk increases significantly)',
        'Suspected plantar fascia rupture on exam',
        'Allergy to injectate',
        'Poorly controlled diabetes (relative — consider carefully)',
      ],
    },
    supplies: [
      '25g needle, 1.5 inch',
      '3 mL syringe',
      'Triamcinolone 20–40 mg (0.5–1 mL of 40 mg/mL)',
      'Lidocaine 1% (1–2 mL)',
      'Antiseptic prep',
      'Sterile gloves',
      'Ethyl chloride spray (optional — topical anesthesia)',
      'Bandage',
    ],
    setup: [
      'Patient prone (preferred) or lateral decubitus — prone allows medial approach with foot relaxed',
      'Identify the medial heel — the insertion point of the plantar fascia on the calcaneus',
      'Palpate the point of maximal tenderness (usually anteromedial calcaneus)',
      'Medial approach: enter from medial heel, aim toward the plantar surface — avoids the painful heel pad',
      'Plantar approach: enter from the plantar surface — accurate but painful; not preferred',
      'Ethyl chloride spray can reduce skin discomfort before needle insertion',
    ],
    steps: [
      'Position patient prone with foot hanging off table or lateral decubitus',
      'Palpate and mark the point of maximal tenderness (medial calcaneal tubercle)',
      'Apply ethyl chloride spray if desired; prep skin with antiseptic',
      'Insert 25g needle from the medial aspect of the heel, aiming toward the plantar fascia insertion',
      'Advance needle to periosteal level at the calcaneal insertion',
      'Pull back 2–3 mm into the fascia/peritendinous space',
      'Aspirate briefly; inject with moderate pressure — some resistance is expected',
      'Withdraw needle, apply firm pressure; bandage',
    ],
    videos: [
      { title: 'Plantar Fascia Injection with Cortisone Demonstration', url: 'https://www.youtube.com/watch?v=E5_TlTWMVKI', channel: 'Sports Medicine Ultrasound', note: 'Medial approach technique clearly demonstrated' },
      { title: 'Plantar Fasciitis Injection Treatment for Heel Pain', url: 'https://www.youtube.com/watch?v=FSxuK-gp1Cc', channel: 'Auburn Medical Group', note: 'Practical primary care approach with patient counseling' },
    ],
    aftercare: [
      'No weight bearing for 15–30 minutes post-injection (vasovagal risk from heel tenderness)',
      'Ice to heel 15–20 min TID for 48 hours',
      'Limit standing and walking for 24–48 hours',
      'Continue heel cord and plantar fascia stretching program',
      'Encourage night splint use',
      'Custom orthotics referral for long-term support',
      'Return precautions: worsening pain, inability to bear weight (concern for fascia rupture or stress fracture)',
      'Limit to 2–3 lifetime injections — rupture risk increases substantially after that',
    ],
    pearls: [
      '🎯 Medial approach is much better tolerated than plantar approach — use it routinely',
      '💡 Warn patient in advance: "The injection itself will be uncomfortable — it\'s going into a tight, painful area"',
      '⚠️ Plantar fascia rupture is a real complication — present as sudden sharp pain during activity post-injection',
      '📐 Fat pad atrophy occurs with repeated injections — visible as loss of heel cushioning',
      '✅ Ultrasound guidance improves accuracy and reduces fat pad injection risk — consider for repeat injections',
      '⏱️ Common mistake: injecting too plantar (into heel fat pad) — always aim from medial side',
      '🔍 Don\'t forget: most plantar fasciitis resolves within 12 months without injection — counsel on conservative measures first',
    ],
  },

  // ─────────────────────────── DERMATOLOGY ───────────────────────────

  {
    id: 'shave-biopsy',
    title: 'Shave Biopsy',
    category: 'Dermatology',
    categoryId: 'derm',
    overview: {
      description:
        'Superficial removal of a skin lesion using a razor blade or scalpel blade held tangentially to the skin surface. Best for raised, benign-appearing lesions and suspected basal/squamous cell carcinomas. Not appropriate for suspected melanoma (depth needed for Breslow staging).',
      indications: [
        'Raised or pedunculated lesions (seborrheic keratosis, dermal nevi)',
        'Suspected basal cell or squamous cell carcinoma',
        'Lesions likely to have benign diagnosis where complete excision not needed',
        'Pyogenic granuloma',
        'Acrochordon (skin tag) — larger ones',
      ],
      contraindications: [
        'Suspected melanoma — NEVER shave biopsy (depth cannot be assessed)',
        'Deep lesions requiring full-thickness specimen',
        'Bleeding disorder without adequate correction',
        'Active infection at biopsy site',
      ],
    },
    supplies: [
      'Disposable razor blade (DermaBlade preferred) or #15 scalpel blade',
      'Lidocaine 1% with epinephrine (1:100,000)',
      '27–30g needle, 1 mL syringe',
      'Aluminum chloride 20% (Drysol) or Monsel\'s solution for hemostasis',
      'Cotton-tipped applicators',
      'Specimen container with formalin',
      'Marking pen',
      'Gloves, gauze',
    ],
    setup: [
      'Patient positioned for optimal access to the lesion site',
      'Good lighting is essential — consider a headlamp or exam light repositioned',
      'Mark the lesion border before injecting (anesthetic distorts tissue)',
      'Inject anesthetic subdermally, creating a bleb that elevates the lesion',
      'The bleb "pedestals" the lesion, making the shave easier and more controlled',
      'Ensure adequate anesthesia — test with light pinch before starting',
    ],
    steps: [
      'Mark lesion borders; inject local anesthetic to create a raised bleb',
      'Wait 3–5 minutes for full anesthetic effect and vasoconstriction',
      'Hold DermaBlade or scalpel blade parallel to skin surface, with gentle tension on skin',
      'Use horizontal sawing motion to shave through base of lesion',
      'Keep blade nearly flat (10–15°) to avoid going too deep',
      'Achieve hemostasis: aluminum chloride on cotton tip, apply firm pressure for 30–60 sec',
      'Place specimen in formalin-filled container immediately',
      'Apply thin film of petroleum jelly and non-adherent dressing',
    ],
    videos: [
      { title: 'The Shave Biopsy', url: 'https://www.youtube.com/watch?v=VzfUo1P1KR0', channel: 'Rendon Center', note: 'Clean technique demonstration with DermaBlade' },
      { title: 'How to Choose the Right Skin Biopsy Technique', url: 'https://www.youtube.com/watch?v=gaatzYqMPG8', channel: 'HealthCert Education', note: 'Comparison of shave vs punch biopsy — good for decision-making' },
    ],
    aftercare: [
      'Wound heals by secondary intention — no sutures needed',
      'Apply petroleum jelly (Vaseline) daily and cover with bandage',
      'Moist wound healing → faster healing, less scarring than "dry" wound care',
      'Shower/clean wound daily; keep moist until healed (typically 1–3 weeks)',
      'Expect slight oozing first 24–48 hours — reassure patient',
      'Return precautions: wound not healing by 3–4 weeks, signs of infection, increasing pain',
      'Follow up on pathology results — communicate plan to patient before they leave',
    ],
    pearls: [
      '🎯 The anesthetic bleb is your friend — it elevates the lesion and makes the shave more controlled',
      '💡 Epi in the anesthetic reduces bleeding dramatically — use it unless contraindicated',
      '⚠️ Never shave-biopsy a lesion suspicious for melanoma — depth assessment is critical',
      '📐 If bleeding doesn\'t stop with aluminum chloride: try Monsel\'s or silver nitrate; avoid cautery on the face',
      '✅ DermaBlade > scalpel for most shave biopsies — more flexibility and control',
      '⏱️ Common mistake: going too deep and creating a crater — keep blade nearly flat and let the bleb do the work',
      '🔍 Moist wound care consistently outperforms dry care — spend 30 seconds counseling patients on Vaseline application',
    ],
  },

  {
    id: 'punch-biopsy',
    title: 'Punch Biopsy',
    category: 'Dermatology',
    categoryId: 'derm',
    overview: {
      description:
        'Full-thickness skin biopsy using a circular cutting punch (typically 3–4 mm). Provides a core of skin through the epidermis, dermis, and subcutaneous fat — essential for accurate histopathological diagnosis. The technique of choice for inflammatory dermatoses and suspected melanocytic lesions.',
      indications: [
        'Suspected melanoma or atypical pigmented lesion (preferred over shave)',
        'Inflammatory dermatoses (psoriasis, lichen planus, lupus)',
        'Undiagnosed rash or skin condition requiring full-thickness diagnosis',
        'Acneiform or follicular lesions',
        'Suspected vasculitis',
      ],
      contraindications: [
        'Active infection at site',
        'Coagulopathy (relative)',
        'Lesions on digits/genitals without experience (vascular anatomy risk)',
        'Poor wound healing history without preparation',
      ],
    },
    supplies: [
      '3 mm or 4 mm punch biopsy tool (disposable preferred)',
      'Lidocaine 1% with epinephrine (avoid epi on digits/nose/penis)',
      '27–30g needle, 1 mL syringe',
      'Iris scissors or tissue scissors',
      'Forceps (toothed — Adson or pick-up)',
      '3-0 or 4-0 absorbable suture (Vicryl) or non-absorbable (Prolene/Nylon)',
      'Specimen container with formalin',
      'Gauze, antiseptic prep',
    ],
    setup: [
      'Identify dominant skin tension lines (relaxed skin tension lines — RSTLs)',
      'Orient the punch along the RSTLs for a linear closure',
      'Mark the lesion center or most representative area',
      'Inject local anesthetic subdermally — do NOT distort a melanocytic lesion with large bleb',
      'Stretch skin perpendicular to RSTLs before punching — creates an oval defect that closes as a line',
      'Confirm anesthesia before starting',
    ],
    steps: [
      'Mark lesion; inject local anesthetic',
      'Stretch skin perpendicular to relaxed skin tension lines',
      'Place punch tool centered on lesion; apply firm downward pressure while rotating',
      'Punch to adequate depth (through dermis into fat — you\'ll feel the resistance change)',
      'Lift core with forceps at the edge (NOT through the center of the lesion)',
      'Use scissors to cut the base of the core at the fat level',
      'Place specimen immediately into formalin',
      'Close with 1–2 interrupted sutures (3-0 or 4-0 depending on location)',
      'Apply pressure, bandage',
    ],
    videos: [
      { title: 'Punch Biopsy Demo', url: 'https://www.youtube.com/watch?v=cdL5VhK6sYQ', channel: 'Skilled Physicians Group', note: 'Full punch biopsy demonstration including closure' },
      { title: 'Punch Biopsy', url: 'https://www.youtube.com/watch?v=EogpSfUUlyA', channel: "St John's Derm Academy", note: 'Clear technique with RSTL orientation guidance' },
    ],
    aftercare: [
      'Suture site: keep clean and dry for 24–48 hours, then gentle cleaning daily',
      'Suture removal: face 5–7 days, scalp 7 days, trunk/extremities 10–14 days',
      'Apply petroleum jelly under dressing to prevent suture from sticking',
      'No swimming or soaking until sutures removed',
      'Return precautions: signs of infection (pus, expanding redness, increasing warmth, fever)',
      'Follow up on pathology — set expectations for 5–10 business days before results',
    ],
    pearls: [
      '🎯 Stretch perpendicular to RSTLs before punching — you\'ll get an oval that closes to a clean line',
      '💡 Never grab the center of a melanocytic lesion with forceps — use the edge to preserve histology',
      '⚠️ For facial or cosmetically sensitive sites: use smallest effective punch (3 mm) and close carefully',
      '📐 Punch must go through fat to get an adequate dermal specimen — feel for the resistance change',
      '✅ For very small punches (3 mm): sometimes no suture needed, heals by secondary intention well',
      '⏱️ Common mistake: crushing the specimen with forceps — always lift at the edge',
      '🔍 Label specimens carefully if doing multiple biopsies — right arm vs. left, site 1 vs. site 2, etc.',
    ],
  },

  {
    id: 'cryotherapy',
    title: 'Cryotherapy',
    category: 'Dermatology',
    categoryId: 'derm',
    overview: {
      description:
        'Controlled destruction of tissue using liquid nitrogen (LN2) spray or cryoprobe. Used for a wide variety of benign and premalignant skin lesions. Mechanism: ice crystal formation causes intracellular and extracellular damage, vascular disruption, and eventual cell death.',
      indications: [
        'Actinic keratosis (AK)',
        'Common and plantar warts (verruca vulgaris, verruca plantaris)',
        'Seborrheic keratosis',
        'Molluscum contagiosum',
        'Condyloma acuminata (genital warts)',
        'Benign epidermal lesions (small nevi, lentigines — use caution)',
      ],
      contraindications: [
        'Pigmented lesion suspicious for melanoma — do NOT freeze',
        'Area with poor circulation (peripheral vascular disease, Raynaud\'s)',
        'Lesion over peripheral nerve (risk of nerve damage)',
        'Cold urticaria or cold agglutinin disease',
        'Patient with cryoglobulinemia',
        'Non-sun-exposed dark skin (hypopigmentation risk)',
      ],
    },
    supplies: [
      'Liquid nitrogen supply (cryogun or dewar)',
      'Spray tip appropriate for lesion size',
      'Otoscope cones or styrofoam cup as shield for adjacent normal skin (optional)',
      'Timer',
      'Gloves',
      'Gauze',
    ],
    setup: [
      'Inspect lesion carefully — confirm it\'s appropriate for cryotherapy',
      'Warn patient of sensation: burning cold, then pain that may last 30–60 minutes',
      'For actinic keratoses: palpate to ensure you\'re treating the entire lesion, not just the visible portion',
      'Set freeze-thaw cycle plan: AKs typically need 1–2 cycles; warts may need 2–3 cycles',
      'Use styrofoam cup or otoscope cone to shield eyes or sensitive adjacent skin if needed',
      'Measure lesion and plan freeze margin (1–2 mm halo beyond lesion for AKs)',
    ],
    steps: [
      'Confirm diagnosis and patient consent for possible blistering, scarring',
      'Position cryogun 1–2 cm from lesion; use appropriate spray tip',
      'Apply LN2 in 5–10 second bursts, maintaining 1–2 mm ice halo beyond lesion margins',
      'Watch for ice ball to form and spread',
      'Time the freeze cycle: AKs 5–10 sec; warts 10–20 sec; thicker lesions longer',
      'Allow complete thaw (30–60 seconds) before second cycle if indicated',
      'Repeat freeze-thaw cycle for warts (usually 2–3 cycles per session)',
      'Document number of cycles, freeze time, and lesion response',
    ],
    videos: [
      { title: 'Why Cryotherapy Should be Your No. 1 Tool — Liquid Nitrogen', url: 'https://www.youtube.com/watch?v=iIVm9xFSObA', channel: 'Dr Finbar', note: 'Practical GP-level cryotherapy technique and tips' },
      { title: 'Cryotherapy How-To', url: 'https://www.youtube.com/watch?v=TGSkPfPYrrI', channel: 'Jacob Levitt', note: 'Step-by-step liquid nitrogen application technique' },
    ],
    aftercare: [
      'Blistering is expected within 24 hours — reassure patient this is normal healing',
      'Do NOT pop blisters — let them reabsorb',
      'Apply petroleum jelly and non-stick bandage daily until healed',
      'Area may remain red, crusted, or swollen for 1–3 weeks',
      'Hypopigmentation is common and may be permanent, especially in darker skin types',
      'Return precautions: signs of infection, large blistering/pain beyond expected, incomplete healing at 4 weeks',
      'Warts: may need repeat treatment in 3–4 weeks; counsel on realistic expectations (warts are often recalcitrant)',
    ],
    pearls: [
      '🎯 AK treatment: feel the lesion, not just see it — palpable field change needs broader treatment',
      '💡 The "halo" matters: freeze 1–2 mm beyond the visible edge for AKs to treat subclinical cells',
      '⚠️ Wart treatment can require 3–6 sessions over months — set realistic expectations up front',
      '📐 Never freeze lesions suspicious for melanoma — cryotherapy destroys the tissue needed for diagnosis',
      '✅ Combination therapy for warts: salicylic acid between visits improves outcomes',
      '⏱️ Common mistake: under-treating AKs with too short a freeze time — aim for adequate ice ball formation',
      '🔍 Hypopigmentation on darker skin types is a real risk — counsel patients before treating cosmetically sensitive areas',
    ],
  },

  {
    id: 'skin-tag-removal',
    title: 'Skin Tag Removal',
    category: 'Dermatology',
    categoryId: 'derm',
    overview: {
      description:
        'Removal of acrochordons (skin tags) — benign fibroepithelial polyps — by snip excision, cryotherapy, or ligation. Quick, common outpatient procedure. Primarily cosmetic. Clarify billing and coverage with patient beforehand (often not covered by insurance).',
      indications: [
        'Patient request for removal (cosmetic)',
        'Irritation from clothing or friction',
        'Repeated bleeding from trauma',
        'Patient/provider desire to rule out other lesion',
      ],
      contraindications: [
        'Lesion not confirmed as benign skin tag — send to pathology if uncertain',
        'Bleeding disorder without correction',
        'Anticoagulation (relative)',
        'Overlying skin infection',
      ],
    },
    supplies: [
      'Iris scissors or sharp tissue scissors',
      'Forceps or hemostat',
      'Lidocaine 1% with epinephrine (for larger tags)',
      '27–30g needle',
      'Aluminum chloride or silver nitrate for hemostasis',
      'Cotton swabs',
      'Gauze',
      'Specimen container if sending to pathology',
    ],
    setup: [
      'Inspect lesion to confirm it is a true skin tag (pedunculated, flesh-colored, benign appearance)',
      'Assess size: very small tags (< 2 mm) may not need anesthetic; larger ones benefit from local injection',
      'For snip excision: grasp tag at its base with forceps/hemostat',
      'Anesthetic: inject tiny amount at base of stalk, raising small bleb',
      'If multiple tags in one area: inject field block to reduce number of needle sticks',
    ],
    steps: [
      'Confirm diagnosis; obtain consent (document cosmetic nature and billing implications)',
      'For larger tags: inject small amount of local anesthetic at base',
      'Grasp tag at its base with fine forceps or hemostat',
      'Using iris scissors, snip cleanly through the base of the stalk',
      'Apply aluminum chloride on cotton tip to achieve hemostasis with gentle pressure',
      'Inspect the wound: should be flat or slightly depressed',
      'Send to pathology if clinical uncertainty; otherwise discard',
      'Apply small bandage',
    ],
    videos: [
      { title: 'Skin Tag Removal | Watch the Procedure', url: 'https://www.youtube.com/watch?v=oUYhWPTgtao', channel: 'Skin Surgery Clinic', note: 'Snip excision technique clearly demonstrated' },
      { title: 'Skin Tag Removal', url: 'https://www.youtube.com/watch?v=F9AkgsPjIGA', channel: 'Hippo Education', note: 'Primary care-focused overview of technique and coding' },
    ],
    aftercare: [
      'Small wounds: bandage for 24–48 hours; no special wound care needed',
      'Expect mild soreness and redness at site for 2–3 days',
      'Return precautions: signs of infection, excessive bleeding',
      'Counsel: skin tags may recur in the same area over time',
      'New or growing lesions should always be re-evaluated',
    ],
    pearls: [
      '🎯 Tiny skin tags (1–2 mm): no anesthetic needed — quick snip with sharp scissors is fast and well-tolerated',
      '💡 For multiple tags in one session: inject a field block to reduce total needle sticks',
      '⚠️ Never assume a lesion is a skin tag without inspection — send atypical-appearing lesions to pathology',
      '📐 Cut through the stalk cleanly at the base — avoid leaving a remnant that may re-grow or become irritated',
      '✅ Aluminum chloride is highly effective for hemostasis on snip excisions',
      '⏱️ Common mistake: not documenting cosmetic nature and insurance limitations before removing — patient may get surprised bill',
    ],
  },

  {
    id: 'incision-drainage',
    title: 'Incision & Drainage',
    category: 'Dermatology',
    categoryId: 'derm',
    overview: {
      description:
        'Incision into a fluctuant abscess cavity followed by drainage and irrigation. Standard of care for skin and soft tissue abscesses. Antibiotics are NOT routinely needed post-I&D in uncomplicated abscesses in immunocompetent patients (per NEJM NEJM Idsa guidelines).',
      indications: [
        'Fluctuant skin abscess (palpable fluid-filled cavity with surrounding induration)',
        'Pilonidal cyst abscess',
        'Infected epidermoid/sebaceous cyst',
        'Bartholin gland abscess (Word catheter drainage)',
        'Paronychia',
      ],
      contraindications: [
        'Non-fluctuant cellulitis without abscess component — treat medically first',
        'Abscess overlying major vessel or facial danger triangle — consider referral',
        'Suspected hidradenitis suppurativa — management differs',
        'Peritonsillar abscess — refer to ENT',
        'Immunocompromised patient with rapidly spreading infection — may need IV antibiotics first',
      ],
    },
    supplies: [
      '#11 or #15 scalpel blade + handle',
      'Lidocaine 1% with epinephrine; consider buffered lidocaine (mix 9:1 with bicarb for less burn)',
      '10–20 mL syringe + 18–20g needle for anesthetic infiltration',
      'Curved hemostat or Kelly clamp',
      'Wound irrigation syringe (60 mL) + normal saline or sterile water',
      'Packing material: plain gauze strips (NuGauze or plain) — iodoform optional',
      'Culture swab if indicated',
      'Specimen cup',
      'Gloves (doubled), gown, eye protection — these abscesses spray',
    ],
    setup: [
      'Patient positioned for optimal access — abscess should be in a dependent position when possible',
      'Protective equipment for you and staff: gown, goggles — abscess drainage is aerosol-generating',
      'Identify the point of maximal fluctuance — this is your incision site',
      'Ring block or direct infiltration with buffered lidocaine: anesthetic WORKS even in inflamed tissue; inject into normal tissue surrounding the abscess',
      'Do NOT inject directly into the abscess cavity (distributes bacteria, inadequate effect)',
      'Allow full vasoconstriction (3–5 min) before incising',
    ],
    steps: [
      'Adequate anesthesia: ring block or local infiltration around abscess (not into cavity)',
      'Incise with #11 blade at point of maximal fluctuance in a direction parallel to skin tension lines',
      'Make incision long enough to allow complete drainage and exploration (1–2 cm or length of cavity)',
      'Express initial drainage with gentle pressure; send culture if immunocompromised or MRSA risk',
      'Insert curved hemostat and break up loculations by opening and spreading',
      'Irrigate copiously with saline via syringe — volume matters (minimum 100–200 mL)',
      'Pack wound loosely with plain gauze strip to prevent premature closure',
      'Apply absorptive dressing over packing',
      'Arrange wound check in 48–72 hours for packing removal and reassessment',
    ],
    videos: [
      { title: 'How to do Incision and Drainage of an Abscess', url: 'https://www.youtube.com/watch?v=5lqtonfXDPw', channel: 'NP Skills Made Easy', note: 'Full technique including packing and wound care' },
      { title: 'Basic Principles of Incision and Drainage', url: 'https://www.youtube.com/watch?v=v74J27V5hKE', channel: 'TaughtWell SimplifyEd', note: 'Excellent overview of I&D principles and common pitfalls' },
    ],
    aftercare: [
      'Return in 48–72 hours for packing removal and wound check',
      'Warm soaks or warm compresses 3–4x daily after packing is removed',
      'Wound heals from the inside out — reassure patient about open wound appearance',
      'Antibiotics: NOT routinely needed for uncomplicated abscess post-I&D (recent evidence)',
      'TMP-SMX or doxycycline: consider if MRSA risk, systemic symptoms, or immunocompromise',
      'Return precautions BEFORE packing removal: spreading redness, fever >101°F, red streaking',
      'Complete healing: 2–4 weeks depending on cavity size',
    ],
    pearls: [
      '🎯 The #11 blade is your best friend — pointed tip allows precise entry into the cavity',
      '💡 Break up loculations aggressively with the hemostat — the biggest cause of recurrence is incomplete drainage',
      '⚠️ Wear protective gear — these abscesses are under pressure and WILL spray',
      '📐 Packing: loose is better than tight — packing too tightly slows healing and causes pain',
      '✅ Loop drainage technique: pass a penrose drain through the cavity via two counter-incisions — reduces need for repacking and is well-tolerated',
      '⏱️ Common mistake: incision too small — you need adequate length to explore the full cavity',
      '🔍 "Anesthesia doesn\'t work in infected tissue" is a MYTH — ring block works very well; inject into the surrounding normal tissue',
    ],
  },

  {
    id: 'toenail-removal',
    title: 'Toenail Removal',
    category: 'Dermatology',
    categoryId: 'derm',
    overview: {
      description:
        'Partial or total avulsion of the toenail for ingrown toenail, onychocryptosis, or onychomycosis. Partial nail avulsion with phenol matricectomy offers superior outcomes for recurrent ingrown toenails compared to avulsion alone. One of the most commonly performed office procedures in family medicine.',
      indications: [
        'Ingrown toenail (onychocryptosis) with infection, granulation tissue, or recurrence',
        'Failed conservative treatment (packing, splinting, warm soaks)',
        'Chronic or recurrent ingrown toenail',
        'Subungual hematoma drainage',
        'Severe onychomycosis with nail dystrophy',
      ],
      contraindications: [
        'Peripheral arterial disease (relative — consult vascular surgery)',
        'Diabetes with peripheral neuropathy or poor circulation (relative — caution)',
        'Active cellulitis extending proximal to nail — treat infection first',
        'Phenol matricectomy: avoid in allergic patients',
        'Coagulopathy',
      ],
    },
    supplies: [
      'Lidocaine 1% WITHOUT epinephrine (digital block — NO epi on digits)',
      '27–30g needle, syringe',
      'Straight iris scissors or nail splitter',
      'Straight hemostat or Kelly clamp',
      'Freer elevator or flat periosteal elevator',
      'Phenol 88% + neutralizing agent (isopropyl alcohol) for matricectomy',
      'Cotton-tipped applicators',
      'Tourniquet: Penrose drain or finger trap',
      'Petroleum gauze dressing',
    ],
    setup: [
      'Digital block: inject 1–2 mL lidocaine WITHOUT epinephrine at the medial and lateral base of the toe (2-point digital block)',
      'Confirm complete anesthesia before proceeding: test with needle prick',
      'Apply digital tourniquet (Penrose drain wrapped at toe base) — improves visualization and reduces bleeding',
      'Clean nail with antiseptic',
      'For partial avulsion: decide which border is affected (usually lateral)',
      'Have phenol ready if performing matricectomy',
    ],
    steps: [
      'Perform digital block; confirm full anesthesia',
      'Apply digital tourniquet',
      'Use straight scissors or nail splitter to cut nail longitudinally from distal edge to matrix, 2–3 mm from lateral fold',
      'Use hemostat to grip the nail segment firmly',
      'Twist hemostat in one smooth motion while pulling upward to avulse the nail segment',
      'Inspect nail matrix; ensure complete removal of nail plate from matrix',
      'For phenol matricectomy: apply 88% phenol to matrix with cotton-tipped applicator for 30 sec × 3 applications',
      'Neutralize phenol with 70% isopropyl alcohol',
      'Irrigate with saline; release tourniquet',
      'Apply petroleum gauze and absorptive dressing',
    ],
    videos: [
      { title: 'Ingrown Toenail Removal — Permanent, Cosmetic Procedure', url: 'https://www.youtube.com/watch?v=g-FQPoW_bSQ', channel: 'Dr. Robert J. Moore III', note: 'Complete nail avulsion with phenol matricectomy' },
      { title: 'Injection for Ingrown Toenail (Digital Block)', url: 'https://www.youtube.com/watch?v=EzuTtJEk75Q', channel: 'Danielle Edwards', note: 'Digital block technique — critical pre-procedure step' },
    ],
    aftercare: [
      'Expect throbbing pain for 12–24 hours after anesthetic wears off — NSAIDs or acetaminophen',
      'Soak in warm water or Epsom salt twice daily starting 24–48 hours post-procedure',
      'Apply petroleum jelly and non-adherent dressing daily',
      'No restrictive footwear for 2–4 weeks; open-toe shoes recommended',
      'Expected: some oozing and redness for 1–2 weeks; drainage is normal',
      'Nail regrowth: 6–12 months',
      'Phenol matricectomy recurrence rate: ~5% — much better than avulsion alone (~60%)',
      'Return precautions: spreading cellulitis, fever, significant purulent drainage',
    ],
    pearls: [
      '🎯 NO epinephrine on digits — EVER. Use plain lidocaine for digital blocks',
      '💡 Confirm full anesthesia before starting — test with needle. Patients will tell you if they feel it',
      '⚠️ Phenol is caustic — protect surrounding tissue; neutralize thoroughly with alcohol',
      '📐 Cut all the way to the matrix before pulling — incomplete cuts lead to a jagged avulsion and more pain',
      '✅ Tourniquet improves visibility significantly — always use it for this procedure',
      '⏱️ Common mistake: inadequate digital block — 2-point block (medial AND lateral) is required, not just one side',
      '🔍 If granulation tissue is present: silver nitrate application at the follow-up visit can accelerate healing',
    ],
  },

  {
    id: 'dermoscopy',
    title: 'Dermoscopy Basics',
    category: 'Dermatology',
    categoryId: 'derm',
    overview: {
      description:
        'Non-invasive technique using a dermatoscope to visualize skin structures beyond what is visible to the naked eye. Significantly improves diagnostic accuracy for melanocytic lesions, basal cell carcinoma, and other skin conditions. Essential skill for family medicine physicians performing skin surveillance.',
      indications: [
        'Evaluation of pigmented lesions (moles, lentigines)',
        'Differentiate melanoma from benign nevi',
        'Assess basal cell carcinoma features',
        'Evaluate seborrheic keratoses vs. melanoma',
        'Diagnose dermoscopic features of actinic keratosis',
        'Vascular lesion characterization',
      ],
      contraindications: [
        'No true contraindications to examination',
        'Dermoscopy does not replace clinical judgment or biopsy for suspicious lesions',
      ],
    },
    supplies: [
      'Dermatoscope (handheld — Heine Delta 20T, DermLite DL4, or similar)',
      'Contact gel or ultrasound gel (for contact dermoscopy)',
      'Lens wipe for cleaning between patients',
      'Good lighting or examination room light',
      'Camera attachment or smartphone adapter (optional for documentation)',
    ],
    setup: [
      'Contact dermoscopy: apply small amount of gel to lesion; press dermatoscope gently against skin — eliminates surface reflection',
      'Non-contact (polarized) dermoscopy: hold 1–2 cm from skin surface — no gel needed',
      'Start with low magnification to assess overall pattern',
      'Systematically evaluate: overall architecture, color distribution, specific structures',
      'Use a standardized approach (ABCD rule or 3-point checklist) for every lesion',
    ],
    steps: [
      'Apply contact gel or use polarized mode (device-dependent)',
      'Place dermatoscope over lesion with light, even pressure',
      'Assess OVERALL PATTERN first: organized (benign features) vs. disorganized (concerning)',
      'Evaluate COLOR: shades of tan/brown = benign; red, white, blue-black, gray = concerning',
      'Look for specific structures: pigment network, dots, globules, streaks, vascular patterns',
      'Apply 3-point checklist: atypical network, blue-white veil, atypical vascular pattern — 2+ = biopsy',
      'Compare to adjacent normal skin and surrounding lesions',
      'Document findings and decision: monitor vs. biopsy',
    ],
    videos: [
      { title: 'Dermoscopy Basics | MEDILOGIN', url: 'https://www.youtube.com/watch?v=fui23nP3zWE', channel: 'MEDILOGIN', note: 'Beginner-friendly dermoscopy pattern recognition overview' },
      { title: 'Introduction to Dermatology | Describing Skin Lesions', url: 'https://www.youtube.com/watch?v=Ijae4iURrDI', channel: 'JJ Medicine', note: 'Systematic lesion description framework for primary care' },
    ],
    aftercare: [
      'Dermoscopy is a diagnostic tool — no wound care needed',
      'Document findings, images if captured, and decision made',
      'If biopsy deferred: establish follow-up timeline (typically 3–6 months for borderline lesions)',
      'Counsel patient: dermoscopy improves accuracy but does not replace biopsy when in doubt',
    ],
    pearls: [
      '🎯 "When in doubt, cut it out" — dermoscopy helps but doesn\'t replace biopsy for suspicious lesions',
      '💡 Two-step algorithm: (1) Is it melanocytic? (2) Is it benign or malignant melanocytic?',
      '⚠️ Blue-white veil = regression/invasion — this is a red flag for melanoma',
      '📐 Atypical pigment network: meshwork with irregular spacing, abrupt ending at periphery = concern',
      '✅ The 3-point checklist is validated for non-expert use — use it consistently for every pigmented lesion',
      '⏱️ Common mistake: not using gel/contact mode — surface glare prevents adequate visualization',
      '🔍 Polarized dermoscopy reveals different structures than contact — some lesions need both modes',
      '📱 Smartphone dermoscopy adapters (MoleScope, Handyscope) are practical in outpatient settings',
    ],
  },

  // ─────────────────────────── WOMEN'S HEALTH ───────────────────────────

  {
    id: 'colposcopy',
    title: 'Colposcopy',
    category: "Women's Health",
    categoryId: 'womens',
    overview: {
      description:
        'Magnified visualization of the cervix using a colposcope following an abnormal Pap smear or positive high-risk HPV test. Used to identify, characterize, and biopsy abnormal transformation zone lesions. A core skill for family medicine physicians who perform comprehensive women\'s health care.',
      indications: [
        'ASC-H, HSIL, or persistent ASC-US/LSIL on Pap smear',
        'Positive high-risk HPV (hrHPV) co-testing with abnormal cytology',
        'Positive HPV 16/18 regardless of cytology',
        'Abnormal-appearing cervix on exam (gross lesion, bleeding, friability)',
        'Persistent low-grade abnormality per current ASCCP guidelines',
      ],
      contraindications: [
        'Active cervicitis or vaginal infection (treat first, then proceed)',
        'Pregnancy: colposcopy is safe, but biopsy indications are modified — avoid ECC in pregnancy',
        'Active menstruation (acceptable but impairs visualization)',
      ],
    },
    supplies: [
      'Colposcope (with green filter capability)',
      'Vaginal speculum (appropriate size)',
      'Acetic acid 3–5% (white vinegar dilution acceptable)',
      'Lugol\'s iodine solution (Schiller\'s test)',
      'Endocervical curette (ECC) — except in pregnancy',
      'Punch biopsy forceps (Tischler, Burke, or Kevorkian)',
      'Monsel\'s solution (ferric subsulfate) for hemostasis',
      'Cotton-tipped applicators, gauze',
      'Specimen containers (one per sample, labeled separately)',
    ],
    setup: [
      'Lithotomy position; bimanual exam first to assess uterine position',
      'Place speculum with adequate exposure of cervix',
      'Apply acetic acid (3–5%) to cervix using cotton ball or applicator; wait 60 seconds',
      'Adjust colposcope: begin at low power (4–6×), assess TZ, then increase magnification',
      'Evaluate the transformation zone (TZ): must see squamocolumnar junction (SCJ) fully (adequate colposcopy)',
      'Use green filter to enhance vascular patterns',
      'Identify acetowhite lesions, mosaicism, punctation, atypical vessels',
      'Plan biopsies: target most abnormal-appearing areas within the TZ',
    ],
    steps: [
      'Bimanual exam; insert speculum; expose cervix',
      'Apply acetic acid ×2; wait 60–90 seconds for acetowhite changes',
      'Survey entire TZ at low power; use green filter',
      'Grade findings: normal, low-grade (thin acetowhite, fine mosaic), high-grade (dense acetowhite, coarse mosaic/punctation)',
      'Target biopsy site(s): most acetowhite, most abnormal vascular pattern, highest-grade appearing area',
      'Inject small amount of local anesthetic at biopsy site (optional but reduces discomfort)',
      'Biopsy with Tischler forceps: firm click = adequate tissue',
      'Place each biopsy in separately labeled formalin container',
      'Perform ECC if SCJ not fully visualized (inadequate colposcopy) — except in pregnancy',
      'Apply Monsel\'s solution to biopsy sites for hemostasis',
      'Apply Lugol\'s iodine if desired (iodine-negative areas = glycogen-poor = abnormal)',
    ],
    videos: [
      { title: 'How to Focus and Use a Colposcope', url: 'https://www.youtube.com/watch?v=wZcr_lUOQhM', channel: 'American Family Physician', note: 'AAFP colposcope operation and technique walkthrough' },
      { title: 'Family Medicine Procedures Clinic | Day in the Life', url: 'https://www.youtube.com/watch?v=ZaALj08_ZOg', channel: 'Zeel Shah MD', note: 'FM resident perspective on in-office colposcopy workflow' },
    ],
    aftercare: [
      'Expected: brown/black discharge for 2–5 days from Monsel\'s solution (reassure patient)',
      'Mild cramping and light spotting for 1–2 days',
      'No intercourse, tampons, or douching for 1 week',
      'Return precautions: heavy bleeding (more than period), fever, foul-smelling discharge, severe pain',
      'Pathology results: typically 7–10 business days; contact patient with results and management plan',
      'Follow current ASCCP guidelines for result-based management',
    ],
    pearls: [
      '🎯 Adequate vs. inadequate colposcopy: can you see the ENTIRE squamocolumnar junction? If no → inadequate → ECC needed',
      '💡 Acetic acid effect develops over 60 seconds — don\'t rush the interpretation',
      '⚠️ Acetowhite = NOT always CIN. Normal immature metaplasia also turns white — grade by density, margins, and vascular pattern',
      '📐 High-grade features: dense, opaque (oyster white) acetowhite, coarse punctation, coarse mosaic, atypical vessels',
      '✅ ASCCP app is invaluable for management decisions at point of care',
      '⏱️ Common mistake: not labeling biopsies separately — site 1 (cervix 12 o\'clock), site 2 (cervix 6 o\'clock), ECC = separate containers',
      '🔍 Never perform ECC in pregnancy — risk of premature labor; biopsies only when indicated',
    ],
  },

  {
    id: 'endometrial-biopsy',
    title: 'Endometrial Biopsy',
    category: "Women's Health",
    categoryId: 'womens',
    overview: {
      description:
        'Office-based aspiration biopsy of the endometrial lining using a small flexible catheter (Pipelle most common). Simple, fast, and effective. Sensitivity of ~80% for endometrial carcinoma; higher in postmenopausal women. Does not require dilation or anesthesia in most cases.',
      indications: [
        'Abnormal uterine bleeding in women ≥45 years',
        'Postmenopausal bleeding (any episode)',
        'Abnormal bleeding in women <45 with risk factors (obesity, PCOS, chronic anovulation)',
        'Thickened endometrium on ultrasound (≥4 mm in postmenopausal)',
        'Suspected endometrial polyp or hyperplasia',
        'Surveillance in hereditary cancer syndromes (Lynch syndrome)',
      ],
      contraindications: [
        'Pregnancy — obtain urine hCG before procedure',
        'Cervical stenosis (relative — may require cervical softening with misoprostol)',
        'Acute PID or cervicitis — treat first',
        'Active bleeding making visualization impossible',
        'Coagulopathy (relative)',
      ],
    },
    supplies: [
      'Pipelle endometrial sampler (or Explora curette)',
      'Vaginal speculum',
      'Ring forceps and cotton balls with betadine',
      'Tenaculum (single-tooth) — optional, for stabilizing cervix',
      'Uterine sound (optional, to measure depth)',
      'Lidocaine 1% for paracervical block (optional)',
      'Specimen container with formalin',
      'Gauze, gloves',
      'NSAIDs pre-procedure (ibuprofen 600–800 mg given 30–60 min prior)',
    ],
    setup: [
      'Pre-procedure: ibuprofen 600–800 mg, 30–60 minutes before procedure',
      'Urine pregnancy test if any possibility of pregnancy',
      'Lithotomy position; bimanual exam to determine uterine position (anteverted vs. retroverted)',
      'Insert speculum; visualize cervix; cleanse with betadine',
      'Optional: paracervical block with lidocaine if anticipated difficulty or stenosis',
      'Apply tenaculum to anterior lip if needed for traction and straightening of uterine axis',
      'Sound the uterus to determine depth (cm) before Pipelle insertion',
    ],
    steps: [
      'Pre-medicate with NSAIDs; confirm negative pregnancy test',
      'Insert speculum; prep cervix with betadine',
      'Apply tenaculum if needed; sound uterine cavity',
      'Insert Pipelle gently through cervical os to fundus — advance to documented depth',
      'Withdraw inner stylette while holding outer sheath in place — creates negative pressure',
      'Rotate 360° while withdrawing the Pipelle from fundus to os, then re-insert and repeat ×2–4',
      'Withdraw Pipelle; expel tissue into formalin container',
      'Ensure adequate tissue (tan/red tissue visible in sample) — if minimal, repeat sampling',
      'Remove tenaculum and speculum; observe briefly for bleeding',
    ],
    videos: [
      { title: 'Endometrial Biopsy', url: 'https://www.youtube.com/watch?v=f59dG84ipg4', channel: 'openmichigan', note: 'Complete Pipelle technique demonstration' },
      { title: 'Pain Management for Endometrial Biopsies', url: 'https://www.youtube.com/watch?v=039OJYhryMk', channel: 'TārāMD', note: 'Practical pain management and paracervical block technique' },
    ],
    aftercare: [
      'Mild cramping for 1–4 hours; NSAIDs for comfort',
      'Light spotting for 1–2 days; normal menstrual flow may follow',
      'No intercourse, tampons, or douching for 48 hours',
      'Return precautions: heavy bleeding, fever, severe pelvic pain',
      'Follow up on results in 7–10 business days; contact patient with plan',
      'Cervical stenosis: if unable to advance Pipelle → consider misoprostol 400–800 mcg vaginally 4–6 hours before next attempt',
    ],
    pearls: [
      '🎯 Pre-treatment with ibuprofen 800 mg significantly reduces pain — always recommend before procedure',
      '💡 Know your uterine position: anteverted vs. retroverted changes the angle of approach — bimanual exam is essential',
      '⚠️ Cervical stenosis (common in postmenopausal women): gentle pressure and slightly different angle; if unable to pass — do NOT force; refer or use misoprostol',
      '📐 Adequate sample: tissue should be visible in the sheath. An adequate Pipelle specimen is tan or red tissue',
      '✅ If minimal sample obtained: EHC/curettage in OR may be needed — do not miss pathology',
      '⏱️ Common mistake: failing to check pregnancy test before starting — rule it out every time',
      '🔍 Sensitivity for endometrial cancer ~80% — if clinical suspicion is high with a negative biopsy, pursue hysteroscopy/D&C',
    ],
  },

  {
    id: 'iud',
    title: 'IUD Insertion & Removal',
    category: "Women's Health",
    categoryId: 'womens',
    overview: {
      description:
        'Intrauterine device (IUD) placement and removal. One of the most effective and underutilized forms of contraception. Family medicine physicians trained in IUD insertion provide critical access in primary care settings. Types: levonorgestrel-releasing (Mirena, Kyleena, Liletta, Skyla) and copper (Paragard).',
      indications: [
        'Contraception — all eligible patients, including nulliparous',
        'Heavy menstrual bleeding (levonorgestrel IUD — Mirena/Liletta)',
        'Endometriosis management (levonorgestrel IUD)',
        'Emergency contraception within 5 days (copper Paragard)',
        'Perimenopause management (levonorgestrel IUD for HRT progestin component)',
      ],
      contraindications: [
        'Confirmed or suspected pregnancy',
        'Unexplained vaginal bleeding (before evaluation)',
        'Active PID or cervicitis (treat first)',
        'Uterine anomaly distorting cavity (fibroids, septum)',
        'Copper IUD: Wilson\'s disease or copper allergy',
        'Current breast or uterine cancer (levonorgestrel IUD — relative)',
        'Uterine cavity < 6 cm or > 9 cm (increased expulsion risk)',
      ],
    },
    supplies: [
      'IUD device (appropriate type per patient preference/indication)',
      'Vaginal speculum',
      'Tenaculum (single-tooth)',
      'Uterine sound',
      'Cervical dilators (optional, for stenotic os)',
      'Ring forceps, cotton balls with betadine',
      'Curved scissors (for trimming strings)',
      'Misoprostol (600–800 mcg PO/vaginally 2–4 hours before — optional for nulliparous)',
      'NSAIDs (ibuprofen 600–800 mg) 30–60 min pre-procedure',
      'Lidocaine 1% for paracervical block (optional but recommended for nulliparous)',
    ],
    setup: [
      'Pre-procedure: NSAIDs 30–60 min prior; consider misoprostol for nulliparous or known stenosis',
      'STI screening should be up to date before insertion',
      'Urine pregnancy test immediately before procedure',
      'Lithotomy position; bimanual exam to confirm uterine size, position, and anatomy',
      'Insert speculum; prep cervix with betadine',
      'Paracervical block: inject 2–3 mL lidocaine at 4 and 8 o\'clock positions at cervicovaginal junction',
      'Apply tenaculum to anterior lip for stabilization; gentle traction straightens uterine axis',
      'Sound uterus — must be 6–9 cm (if < 6 cm, do not insert)',
    ],
    steps: [
      'Bimanual exam, confirm uterine position',
      'Insert speculum; prep with betadine',
      'Paracervical block (strongly recommended); apply tenaculum',
      'Sound uterus and record depth',
      'Load IUD per manufacturer instructions; set depth marker to sounded depth',
      'Insert IUD through cervical os to fundus using "no-touch" technique',
      'Release IUD at fundus per device-specific technique',
      'Withdraw insertion tube; cut strings to 3–4 cm from os',
      'Remove tenaculum; apply pressure if bleeding',
      'Confirm placement: bimanual exam or ultrasound if uncertain',
      'Observe patient 5–10 minutes post-procedure before discharge',
    ],
    videos: [
      { title: 'Animation for Insertion of Mirena IUD', url: 'https://www.youtube.com/watch?v=aVZoH0Pda-4', channel: 'Dr Donald Angstetra', note: 'Clear animated walkthrough of IUD insertion steps' },
      { title: 'How is an IUD Inserted? OB-GYN Explains', url: 'https://www.youtube.com/watch?v=krFt5wss6J0', channel: 'Dr. Lesley Hawkins', note: 'Patient-friendly explanation that doubles as resident review' },
    ],
    aftercare: [
      'Cramping for hours to days — NSAIDs as needed',
      'Spotting for 3–6 months (LNG-IUD); heavier initial periods (copper IUD)',
      'Teach patient to check strings monthly: feel for strings at cervix (especially first few months)',
      'Return if: strings not palpable, severe pain, fever, abnormal discharge',
      'LNG-IUD: irregular bleeding common first 3–6 months; oligomenorrhea/amenorrhea expected long-term',
      'Follow-up: 4–6 weeks to verify position and counsel on ongoing experience',
      'Removal: grasp strings firmly with ring forceps and apply steady traction — rarely needs analgesia',
      'Post-removal: fertility returns within 1 cycle for LNG-IUD; immediately for copper IUD',
    ],
    pearls: [
      '🎯 Paracervical block dramatically reduces insertion pain — use it routinely, especially for nulliparous',
      '💡 No-touch technique: avoid touching the string-loaded portion of the IUD during insertion',
      '⚠️ Fundal placement is critical — partial insertion → increased expulsion and failure',
      '📐 If unable to pass the os: gently advance with dilator or reschedule with misoprostol prep',
      '✅ Point-of-care ultrasound to confirm placement is practical in outpatient FM — do it when uncertain',
      '⏱️ Common mistake: not confirming pregnancy test immediately before procedure — it\'s non-negotiable',
      '🔍 Expulsion rate is higher in nulliparous patients and in the first year — confirm at follow-up visit',
      '🔴 IUD in situ + positive pregnancy test → URGENT referral; do NOT leave IUD in place without counsel',
    ],
  },

  {
    id: 'nexplanon',
    title: 'Nexplanon Insertion & Removal',
    category: "Women's Health",
    categoryId: 'womens',
    overview: {
      description:
        'Subdermal insertion or removal of etonogestrel implant (Nexplanon 68 mg). Single-rod implant placed in the inner upper arm. Highly effective (>99%), long-acting (3 years), and reversible. Radiopaque for easy imaging if localization needed. Training certification typically required through manufacturer.',
      indications: [
        'Long-acting reversible contraception',
        'Patients who prefer non-daily or non-procedure-frequent contraception',
        'Estrogen contraindication (breastfeeding, migraine with aura, VTE history)',
        'Patients with compliance challenges',
      ],
      contraindications: [
        'Known or suspected pregnancy',
        'Active thromboembolic disease (relative)',
        'Unexplained vaginal bleeding (evaluate first)',
        'Progestin-sensitive cancers (breast cancer)',
        'Severe hepatic disease',
        'Allergy to device components',
      ],
    },
    supplies: [
      'Nexplanon insertion kit (pre-loaded applicator)',
      'Lidocaine 1% with epinephrine, 3–5 mL',
      '25–27g needle for anesthetic',
      'Antiseptic prep',
      'Sterile gloves and drape',
      'Sterile adhesive strips (steri-strips)',
      'Pressure bandage wrap (e.g., Coban)',
      'Marking pen and ruler',
      'Removal: #11 scalpel, mosquito hemostat, small curved clamps',
    ],
    setup: [
      'Position: patient supine, non-dominant arm abducted 90° with external rotation (hand behind head)',
      'Identify insertion site: inner aspect of upper arm, 8–10 cm from medial epicondyle',
      'Mark the insertion site and direction of implant (toward shoulder)',
      'Prep skin; maintain sterile field',
      'Inject 2–3 mL lidocaine subdermally along planned insertion track (NOT intramuscularly)',
      'Confirm anesthesia before inserting — pinch the area',
      'Always follow manufacturer-specific training for loaded applicator use',
    ],
    steps: [
      'Position patient; mark site and direction',
      'Prep and drape arm; inject local anesthetic subdermally along insertion tract',
      'Remove needle cover from applicator; verify implant is visible in needle tip window',
      'Insert needle bevel-up at 30° angle, then lower to horizontal once through dermis',
      'Advance needle subdermally to hub while lifting skin with non-dominant hand',
      'Hold applicator steady; unlock and retract slider completely to release implant',
      'Verify implant is NO longer in needle by checking window',
      'Palpate implant under skin — should feel like a firm linear ridge',
      'Apply steri-strips and pressure bandage',
      'Document location in patient chart; provide implant ID card',
    ],
    videos: [
      { title: 'Nexplanon Birth Control Implant Insertion', url: 'https://www.youtube.com/watch?v=KbvUb_1xFcI', channel: 'Gyno Girls', note: 'Step-by-step implant insertion technique with tips' },
      { title: 'Nexplanon Insertion and Removal Training', url: 'https://www.youtube.com/watch?v=LI9YVV250z4', channel: 'OBGYNqueens', note: 'Insertion and removal in one video — great resident resource' },
    ],
    aftercare: [
      'Leave pressure bandage in place for 24–48 hours; keep steri-strips on until they fall off (5–7 days)',
      'Bruising and mild soreness at site is expected',
      'Counsel: irregular bleeding is common, especially first 6–12 months',
      'Amenorrhea is possible and expected — not harmful',
      'Contraceptive effect begins within 24 hours if inserted within 5 days of menses',
      'Replace after 3 years; can be removed at any time before that',
      'Return precautions: signs of infection, implant not palpable, severe arm pain',
      'Difficult removal: if implant cannot be palpated, obtain ultrasound ± X-ray localization before attempting removal',
    ],
    pearls: [
      '🎯 Subdermal is key — if you feel resistance or the arm tents significantly, you\'re going too deep (intramuscular risk)',
      '💡 Check the applicator window before AND after deployment — the white rod should disappear when implant is released',
      '⚠️ Neurovascular structures are at risk with deep insertion — the medial cutaneous nerve of the forearm is nearby',
      '📐 Mark the site with permanent marker before prepping — it\'s easy to lose the spot after draping',
      '✅ Palpate the implant immediately after insertion — if you can\'t feel it, get ultrasound/X-ray before the patient leaves',
      '⏱️ Common mistake: inserting too deep (IM) — maintain horizontal plane after entering the dermis; tenting the skin helps',
      '🔍 Deep or non-palpable implants: refer to trained removal specialist with imaging guidance',
    ],
  },

  {
    id: 'vulvar-biopsy',
    title: 'Vulvar Biopsy',
    category: "Women's Health",
    categoryId: 'womens',
    overview: {
      description:
        'Office-based punch biopsy of vulvar tissue for evaluation of vulvar lesions, suspected VIN (vulvar intraepithelial neoplasia), Paget\'s disease, lichen sclerosus, or other dermatoses. Most representative biopsy from the most abnormal appearing area. Adequate anesthesia is critical — the vulva is highly sensitive.',
      indications: [
        'Suspected vulvar intraepithelial neoplasia (VIN)',
        'Persistent or unexplained vulvar lesions',
        'Lichen sclerosus, lichen planus — confirm diagnosis',
        'Suspected Paget\'s disease of the vulva',
        'Condylomata with atypical features',
        'Vulvar melanoma evaluation',
      ],
      contraindications: [
        'Active infection at biopsy site — treat first',
        'Coagulopathy',
        'Patient unable to tolerate procedure',
      ],
    },
    supplies: [
      '3–4 mm Keyes punch (or standard punch biopsy tool)',
      'Lidocaine 1% with epinephrine (acceptable in labial skin; avoid at clitoris)',
      '27–30g needle, small syringe',
      'Iris scissors or tissue scissors',
      'Small, fine-tipped forceps',
      'Silver nitrate sticks or Monsel\'s solution for hemostasis',
      'Specimen container with formalin',
      'Absorbable suture 4-0 (Vicryl) if closure needed',
    ],
    setup: [
      'Lithotomy position with good lighting — adequate visualization is essential',
      'Apply acetic acid (3–5%) to vulva to highlight acetowhite lesions (vulvoscopy)',
      'Identify most abnormal appearing area for biopsy',
      'Generous local anesthetic is critical — 0.5–1 mL infiltrated subdermally at biopsy site',
      'Wait full 2–3 minutes for lidocaine effect — the vulva requires more time than other skin sites',
      'Confirm anesthesia with gentle pinch before starting',
    ],
    steps: [
      'Position, adequate lighting; apply acetic acid if using vulvoscopy',
      'Infiltrate lidocaine generously; wait 2–3 minutes',
      'Stretch skin gently at biopsy site',
      'Apply Keyes punch with firm downward rotation to desired depth',
      'Lift tissue core with fine forceps, cut base with scissors',
      'Place in formalin; label site carefully (e.g., left labium majus, 2 o\'clock)',
      'Achieve hemostasis with silver nitrate or Monsel\'s',
      'Close with absorbable 4-0 suture if > 4 mm punch or brisk bleeding',
    ],
    videos: [
      { title: 'Gynecologic Procedures: Vulvar Biopsy on Banana Model', url: 'https://www.youtube.com/watch?v=-7vuq0wth3A', channel: 'Teaching Videos', note: 'Hands-on demonstration using simulation model' },
      { title: 'Getting A Vulvar Biopsy', url: 'https://www.youtube.com/watch?v=N1FR95Lk5vo', channel: 'Alexandra Dubinskaya MD', note: 'Patient-centered perspective useful for counseling prep' },
    ],
    aftercare: [
      'Expect soreness and mild bleeding for 24–48 hours',
      'Sitz baths 2–3x daily for comfort and wound healing',
      'Pat dry gently; apply thin layer of petroleum jelly to biopsy site',
      'No intercourse or tampon use until healed (5–7 days)',
      'Absorbable sutures dissolve on their own',
      'Return precautions: heavy bleeding, signs of infection, severe pain',
      'Follow up on pathology results in 7–10 business days',
    ],
    pearls: [
      '🎯 Generous lidocaine is non-negotiable here — the vulva is exquisitely sensitive',
      '💡 Acetic acid application (vulvoscopy) highlights acetowhite lesions and guides biopsy site selection',
      '⚠️ Don\'t underestimate vulvar lichen sclerosus — biopsy is needed for definitive diagnosis and guides treatment',
      '📐 Biopsy the MOST ABNORMAL area — not the easiest to reach; use colposcope if available to guide selection',
      '✅ Label sites precisely in pathology order: location, clock position — this matters for surgical follow-up',
      '⏱️ Common mistake: inadequate anesthesia from impatience — the vulva needs full 2–3 minutes',
      '🔍 Silver nitrate hemostasis on the vulva works well and is well-tolerated',
    ],
  },

  {
    id: 'leep',
    title: 'LEEP (Loop Electrosurgical Excision Procedure)',
    category: "Women's Health",
    categoryId: 'womens',
    overview: {
      description:
        'Excision of the cervical transformation zone using a thin wire loop electrode with electrical current. Provides both diagnosis and treatment for CIN 2/3 (high-grade squamous intraepithelial lesion). Most commonly performed in an outpatient office or procedure suite. LEEP is preferred over cold knife conization in most outpatient settings.',
      indications: [
        'CIN 2 or CIN 3 (high-grade squamous intraepithelial lesion) on colposcopy/biopsy',
        'Persistent CIN 1 ≥2 years',
        'Inadequate colposcopy with high-grade cytology',
        'Positive ECC for CIN 2/3',
        'HSIL cytology with inadequate colposcopy — "see and treat" approach',
      ],
      contraindications: [
        'Pregnancy (ABSOLUTE — risk of preterm labor)',
        'Active cervicitis or PID — treat first',
        'Coagulopathy',
        'Adequate follow-up not possible (relative)',
        'Patient not consenting to procedure',
      ],
    },
    supplies: [
      'LEEP generator unit (electrosurgical)',
      'Loop electrodes (various sizes — 20 mm × 8 mm most common)',
      'Ball electrode for hemostasis',
      'Vaginal speculum with smoke evacuator port (or separate smoke evacuator)',
      'Tenaculum',
      'Paracervical block: lidocaine 1% with epinephrine 1:100,000',
      'Monsel\'s solution',
      'Lugol\'s iodine',
      'Acetic acid 3–5%',
      'Ring forceps, cotton balls',
      'Specimen container with formalin',
      'Patient grounding pad',
      'Smoke evacuator (OSHA required — HPV vapor is aerosolized)',
    ],
    setup: [
      'Pre-procedure: ibuprofen 800 mg 30–60 minutes prior; lorazepam 1–2 mg optional for anxious patients',
      'Pregnancy test immediately before procedure',
      'Lithotomy position; insert smoke-evacuator speculum',
      'Apply paracervical block: 2–3 mL lidocaine with epi at 3, 6, 9, 12 o\'clock positions',
      'Wait 3–5 minutes for full anesthetic effect',
      'Apply acetic acid; survey TZ under colposcope',
      'Apply Lugol\'s to demarcate TZ boundaries',
      'Attach patient grounding pad to thigh',
      'Set generator: cutting 30–45W, coagulation 40–60W (machine-dependent)',
      'Select appropriate loop size to cover TZ in one or two passes',
    ],
    steps: [
      'Confirm paracervical block is adequate; apply Lugol\'s iodine',
      'Activate smoke evacuator before applying current',
      'Enter loop electrode lateral to lesion outside iodine-negative zone',
      'Activate current before contacting tissue; maintain smooth, even pass',
      'Complete loop from one side, arc under the TZ, exit opposite side',
      'Remove specimen immediately with forceps and place in formalin',
      'If TZ extends into canal: perform second "top hat" pass with smaller loop',
      'Hemostasis: Monsel\'s solution (ferric subsulfate) — apply with cotton tip with firm pressure',
      'Cauterize bleeding points with ball electrode as needed',
      'Confirm complete hemostasis before removing speculum',
      'Document loop size(s), current settings, and specimen orientation',
    ],
    videos: [
      { title: 'How to Perform a LEEP (Loop Electrosurgical Excision Procedure)', url: 'https://www.youtube.com/watch?v=RPbb5VL2g0Y', channel: 'American Family Physician', note: 'AAFP official LEEP technique — excellent for FM residents' },
      { title: 'LEEP to Remove Abnormal Cells', url: 'https://www.youtube.com/watch?v=Y-V3rI-3JOg', channel: 'SLUCare', note: 'Academic medical center LEEP walkthrough with narration' },
    ],
    aftercare: [
      'Discharge if vitals stable and no active bleeding (typically 15–20 min observation)',
      'Expected: brownish-black discharge for 1–3 weeks from Monsel\'s (reassure)',
      'No intercourse, tampons, or tub soaking for 4 weeks',
      'Light activity only for 1–2 days; pelvic rest until discharge clears',
      'Return precautions: soaking a pad in <1 hour, fever >101°F, severe cramping, foul odor',
      'Follow-up: repeat Pap + HPV co-testing at 6 months (ASCCP protocol)',
      'Counsel on LEEP and pregnancy: small increased risk of preterm delivery; obstetrics team should be informed',
    ],
    pearls: [
      '🎯 Paracervical block with epi is essential — epi reduces bleeding significantly; use it every time',
      '💡 Pre-medicate with ibuprofen 800 mg AND consider lorazepam 1–2 mg for anxious patients',
      '⚠️ Smoke evacuator is REQUIRED, not optional — HPV DNA is present in LEEP plume',
      '📐 Activate current BEFORE touching tissue — starting the loop against tissue without current causes tearing',
      '✅ Monsel\'s + ball electrode: always have both ready for hemostasis',
      '⏱️ Common mistake: too slow or hesitant loop pass — results in thermal artifact on specimen and char at margins',
      '🔍 "See and treat" is appropriate in HSIL + inadequate colposcopy — avoids delay and second visit',
      '🚨 NEVER perform LEEP on a pregnant patient — absolute contraindication',
    ],
  },

  // ─────────────────────────── AUDIOLOGY ───────────────────────────

  {
    id: 'hearing-screening',
    title: 'Hearing Screening Basics',
    category: 'Audiology',
    categoryId: 'audiology',
    overview: {
      description:
        'Pure tone audiometric screening performed in a primary care outpatient setting to identify patients with hearing loss who require further audiological evaluation. Not a diagnostic test — a pass/fail screening tool. Integral to annual wellness exams, especially for patients ≥50 years, occupational exposure, and ototoxic medication monitoring.',
      indications: [
        'Annual wellness exam for adults ≥50 years (or per USPSTF guidance)',
        'Patient complaint of hearing difficulty or tinnitus',
        'Occupational noise exposure',
        'Ototoxic medication monitoring (aminoglycosides, loop diuretics, cisplatin)',
        'Post-meningitis or otitis media with effusion follow-up',
        'Pediatric developmental screening',
      ],
      contraindications: [
        'No absolute contraindications to screening',
        'Active ear pain or discharge: address before audiometric testing',
        'Impacted cerumen: remove before testing (impairs results significantly)',
      ],
    },
    supplies: [
      'Screening audiometer (e.g., AudioScope 3, Welch-Allyn AudioScope, portable audiometer)',
      'Appropriate ear tip sizes (foam or reusable)',
      'Otoscope (visualize canals before testing)',
      'Cerumen removal tools if impaction noted',
      'Quiet examination room (ambient noise < 40 dB)',
    ],
    setup: [
      'Ensure testing room is quiet — background noise is the #1 source of false-fail results',
      'Perform otoscopy BEFORE audiometry: visualize canal for wax, effusion, perforation',
      'If impacted cerumen is present: address before testing — results will be invalid',
      'Explain the test to patient: "You will hear tones at different pitches — press the button (or raise hand) each time you hear a tone, even if faint"',
      'Insert tip properly: gently tug pinna superiorly and posteriorly to straighten canal; insert until snug seal',
      'Test the "better" ear first to set a baseline',
    ],
    steps: [
      'Perform otoscopy; address cerumen if present',
      'Select appropriate ear tip size and insert into canal',
      'Position patient comfortably; explain test procedure',
      'Run screening protocol: typically 500, 1000, 2000, 4000 Hz at 25 dB HL (standard screening level)',
      'Patient responds to each tone heard',
      'PASS: responds to all tones at 25 dB HL bilaterally',
      'REFER: fails to respond to any tone at 25 dB HL in either ear',
      'Document results in chart with frequencies tested, levels used, and pass/fail by ear',
      'Counsel patient on results and next steps (referral to audiology if fail)',
    ],
    videos: [
      { title: 'Addressing Untreated Age-Related Hearing Loss in Primary Care', url: 'https://www.youtube.com/watch?v=3LyclJohiJk', channel: 'NASEM Health and Medicine Division', note: 'Evidence-based hearing screening in primary care overview' },
      { title: "Physician's Perspective on Hearing Screening", url: 'https://www.youtube.com/watch?v=uJPkwBv6cWY', channel: 'Region 10 ESC', note: 'Primary care physician perspective on office hearing screening' },
    ],
    aftercare: [
      'PASS: reassure; repeat screening at next annual visit or if new symptoms arise',
      'FAIL: refer to audiology for full diagnostic audiogram',
      'Document and communicate results clearly — even a "refer" result without follow-up is inadequate',
      'Counsel on hearing protection for occupational/recreational noise exposure',
      'Discuss hearing aid candidacy if consistent with audiologic findings',
    ],
    pearls: [
      '🎯 Otoscopy FIRST — always visualize the canal before audiometric screening',
      '💡 Room noise is the biggest source of error in office hearing screening — use the quietest room available',
      '⚠️ Cerumen impaction causes conductive hearing loss and WILL cause false-fail results',
      '📐 Insert ear tip with correct technique: pull pinna up and back for adults to straighten the EAC',
      '✅ 4000 Hz is the frequency most sensitive for noise-induced hearing loss — always include it in occupational screening',
      '⏱️ Common resident mistake: not performing otoscopy before testing — missing cerumen is the #1 avoidable error',
      '🔍 Single question screen: "Do you have difficulty hearing?" — sensitivity ~71%, specificity ~71% — still reasonable for quick assessment',
    ],
  },

  {
    id: 'tympanogram',
    title: 'Tympanogram Basics & Interpretation',
    category: 'Audiology',
    categoryId: 'audiology',
    overview: {
      description:
        'Tympanometry measures the compliance (mobility) of the tympanic membrane and middle ear system by varying air pressure in the ear canal. Provides objective data about middle ear function — eustachian tube function, middle ear effusion, TM perforation, and ossicular chain integrity. Essential complement to otoscopy and hearing screening in primary care.',
      indications: [
        'Recurrent acute otitis media',
        'Otitis media with effusion (OME) confirmation',
        'Persistent hearing loss or failed hearing screening',
        'Suspected TM perforation',
        'Eustachian tube dysfunction evaluation',
        'Pre- and post-treatment assessment of middle ear effusion',
        'Cholesteatoma evaluation (indirect)',
      ],
      contraindications: [
        'Active ear canal infection (otitis externa) — may cause pain',
        'Active drainage from ear canal — limits seal and accuracy',
        'Known or suspected TM perforation — MicroTymp provides important data but proceed gently',
      ],
    },
    supplies: [
      'Tympanometer (MicroTymp, Welch-Allyn, or clinic model)',
      'Appropriate probe tip (hermetic seal required)',
      'Otoscope for pre-examination',
      'Disposable probe covers',
    ],
    setup: [
      'Otoscopy first: visualize TM, canal, assess for cerumen, perforation, or foreign body',
      'Select probe tip size: snug fit in canal is required for a valid tympanogram',
      'Tug pinna superiorly and posteriorly (adults) or inferiorly and posteriorly (children < 3 years)',
      'Insert probe tip to create an airtight seal — loss of seal is the most common cause of invalid results',
      'Patient should be still, mouth closed, not swallowing during measurement',
      'The probe emits a tone and varies air pressure automatically — the process takes 2–3 seconds per ear',
    ],
    steps: [
      'Perform otoscopy; confirm no contraindications',
      'Select and insert appropriate probe tip with firm, airtight seal',
      'Instruct patient to sit still, breathe normally, do not swallow or talk',
      'Activate tympanometer — automated pressure sweep occurs',
      'Read peak pressure, compliance (Ytm), and ear canal volume (Vea)',
      'Compare result to normative values; classify as Type A, B, or C',
      'Repeat if result appears invalid (flat line with normal expected exam)',
      'Document both ears; note ear canal volume values',
    ],
    videos: [
      { title: 'Tympanometry — An Overview', url: 'https://www.youtube.com/watch?v=l26_ig1fBD0', channel: 'ENT Flashcards', note: 'Clear overview of Type A, B, and C tympanograms with examples' },
      { title: 'Tympanometry (Impedance Audiometry)', url: 'https://www.youtube.com/watch?v=SWRJCWhdU10', channel: 'Learning ENT', note: 'Technical overview with waveform interpretation' },
    ],
    aftercare: [
      'Tympanometry is non-invasive — no wound care needed',
      'Use results to guide management: effusion, tube status, referral decision',
      'Combine with otoscopy findings — tympanometry data does not replace clinical judgment',
      'Refer to audiology or ENT for abnormal results requiring further workup',
    ],
    pearls: [
      '📊 TYPE A (Normal): Peak at 0 daPa (±50), compliance 0.3–1.6 mL → Normal TM mobility, normal middle ear pressure',
      '📊 TYPE B (Flat): No peak, compliance < 0.3 mL → Middle ear effusion, TM perforation, cerumen impaction — check ear canal volume to distinguish',
      '📊 TYPE C (Negative pressure): Peak shifted negative (< −100 daPa) → Eustachian tube dysfunction, early effusion',
      '🎯 Type B + LOW ear canal volume (< 1.0 mL): middle ear effusion — refer to ENT if persistent > 3 months',
      '🎯 Type B + HIGH ear canal volume (> 2.0 mL): TM perforation or patent PE tube — confirm with otoscopy',
      '⚠️ Probe seal is the most common source of invalid tympanogram — you\'ll see an atypical flat line without the typical sharp response',
      '💡 Tympanometry in infants < 6 months: requires 1000 Hz probe tone (not 226 Hz) — standard tympanometry is unreliable in this age group',
      '⏱️ Common mistake: not distinguishing effusion (Type B, low volume) from perforation (Type B, high volume) — always check the volume number',
    ],
  },

  {
    id: 'microtymp',
    title: 'MicroTymp Setup & Troubleshooting',
    category: 'Audiology',
    categoryId: 'audiology',
    overview: {
      description:
        'The Welch-Allyn MicroTymp is a handheld, portable tympanometer commonly used in family medicine outpatient settings. Combines an otoscope head with a tympanometry unit for quick single-device evaluation. This page covers setup, calibration, common errors, and troubleshooting for clinic staff and residents.',
      indications: [
        'Same as tympanometry — see Tympanogram Basics page',
        'Specifically useful for quick in-room middle ear assessment without a dedicated audiology unit',
        'Well-suited for pediatric well-child visits and acute otitis media evaluation',
      ],
      contraindications: [
        'See Tympanogram Basics — same limitations apply',
        'Do not use if device fails calibration — results will be unreliable',
      ],
    },
    supplies: [
      'Welch-Allyn MicroTymp 3 (or generation equivalent in clinic)',
      'Disposable specula tips (appropriate size)',
      'Battery or power source (confirm charge)',
      'Calibration cavity (usually included with device)',
    ],
    setup: [
      'Power on device: press and hold power button; confirm battery level',
      'Attach appropriate-size disposable specula (most commonly medium or large adult tip)',
      'Self-test/calibration: insert probe into calibration cavity; press calibrate — must PASS before patient testing',
      'Select mode: tympanometry (Tymp) for middle ear assessment',
      'Enter patient demographics if device requires logging',
      'Clean probe tip between patients with alcohol wipe; replace disposable specula each patient',
    ],
    steps: [
      'Power on; confirm battery; run calibration check',
      'Select appropriate speculum size for patient age and canal size',
      'Perform otoscopy using the otoscope head before tympanometry',
      'Switch to tympanometry mode; insert probe into ear canal with snug seal',
      'Activate measurement — device will auto-sweep pressure and produce graph',
      'Read result on screen: peak, compliance value, ear canal volume',
      'Document and classify (Type A/B/C) — see Tympanogram Interpretation page',
      'Repeat if invalid seal or atypical trace',
    ],
    videos: [
      { title: 'Welch Allyn MicroTymp 2 — Device Operation', url: 'https://www.youtube.com/watch?v=4jOnhR6Ql-c', channel: 'JOS MEDICAL EXPORT', note: 'MicroTymp device setup and operation walkthrough' },
      { title: 'Tympanometry — An Overview', url: 'https://www.youtube.com/watch?v=l26_ig1fBD0', channel: 'ENT Flashcards', note: 'Review tympanogram interpretation alongside device use' },
    ],
    aftercare: [
      'After testing: remove and dispose of specula; clean probe with dry wipe (not alcohol directly on probe)',
      'Store device in protective case; confirm battery level and recharge as needed',
      'Log any device errors or calibration failures in device log for maintenance tracking',
    ],
    pearls: [
      '🔧 TROUBLESHOOTING — Probe Seal Issues:',
      '• "Check Probe" or seal error: tip not seated in canal — reinsert with pinna traction; try larger tip size',
      '• Patient moving or swallowing during test: re-instruct patient; retry',
      '• Canal is too large for tip: use next size up; foam tip adapters can help',
      '• Result looks atypical: perform otoscopy to rule out obstruction or canal collapse',
      '🔧 TROUBLESHOOTING — Cerumen Impaction:',
      '• Type B result with normal otoscopy appearance: suspect cerumen obscuring view or packed against TM',
      '• Remove cerumen first before repeat testing — results will be unreliable with occlusive wax',
      '• After cerumen removal: always re-test tympanogram and compare',
      '🔧 TROUBLESHOOTING — Device Calibration Failure:',
      '• Run calibration in cavity — if fails: clean cavity and probe; retry',
      '• If repeated calibration failure: document and remove from service; use alternate device',
      '• Do NOT use device that fails calibration — results are unreliable',
      '🔧 TROUBLESHOOTING — Flat Tracing (Type B):',
      '• Check ear canal volume: LOW (< 1 mL) = effusion. HIGH (> 2 mL) = perforation/PE tube',
      '• If volume is normal but tracing flat: consider probe seal issue vs. true pathology',
      '• Always correlate with otoscopy findings',
      '⚠️ Common Resident Mistakes with Hearing Equipment:',
      '• Not doing otoscopy first — misses cerumen, PE tubes, perforations that change interpretation',
      '• Using wrong probe tip size — loose seal produces invalid results every time',
      '• Not restating results in both ears and not comparing — asymmetric results are a referral indication',
      '• Accepting a Type B result without checking ear canal volume — this changes your diagnosis',
      '• Not calibrating at start of session — running tests with uncalibrated equipment',
      '• Performing standard (226 Hz) tympanometry in infants < 6 months — requires 1000 Hz probe tone',
    ],
  },

  // ──────────────────────────────── POCUS ────────────────────────────────
  {
    id: 'pocus-aorta',
    title: 'Abdominal Aorta POCUS',
    category: 'POCUS',
    categoryId: 'pocus',
    overview: {
      description:
        'Focused bedside ultrasound to measure the anteroposterior (AP) diameter of the abdominal aorta and screen for abdominal aortic aneurysm (AAA). A critical, potentially life-saving scan in patients presenting with back/flank pain, hypotension, or unexplained syncope. Normal aorta < 3 cm; AAA ≥ 3 cm; surgical emergency ≥ 5 cm.',
      indications: [
        'Unexplained hypotension or shock in patients > 50 years',
        'Acute back, flank, or abdominal pain in high-risk patients (male, smoker, HTN, family history)',
        'AAA screening in males 65–75 who have ever smoked (USPSTF Grade B)',
        'Pulsatile abdominal mass on exam — confirm with POCUS',
        'Risk stratification in syncope with abdominal symptoms',
        'Monitoring known AAA size over time in collaboration with vascular',
      ],
      contraindications: [
        'No absolute contraindications to diagnostic ultrasound',
        'If AAA rupture strongly suspected on clinical grounds — do not delay emergent surgical consultation for imaging',
        'Morbid obesity or excessive bowel gas may significantly limit visualization (proceed to CT if inadequate)',
        'Uncooperative patient limiting adequate probe placement (relative)',
      ],
    },
    supplies: [
      'Ultrasound machine with low-frequency curvilinear/phased array probe (2–5 MHz)',
      'Ultrasound gel',
      'Probe cover/probe wipes for cleaning between patients',
      'Patient gown or draping for abdominal exposure',
    ],
    setup: [
      'Patient position: supine, abdomen fully exposed from xiphoid to pelvis',
      'Fasting not required but reduces bowel gas artifact if time permits',
      'Select curvilinear probe (2–5 MHz); set depth to 15–20 cm initially; abdominal preset',
      'Apply gel in longitudinal stripe from xiphoid to umbilicus',
      'Room lighting: dim enough to see screen clearly',
      'If excessive bowel gas: gentle steady pressure with probe can displace gas; try different windows',
    ],
    steps: [
      'Start in TRANSVERSE view — probe indicator pointing to patient\'s RIGHT, placed just below xiphoid',
      'Identify aorta: midline, pulsatile, thick-walled, non-compressible round structure; IVC is to RIGHT and compresses easily',
      'Measure AP diameter from outer wall to outer wall at widest point in transverse view',
      'Slide probe inferiorly to just above umbilicus — measure again at this level',
      'Rotate probe 90° to LONGITUDINAL (sagittal) view — confirm aorta throughout its course',
      'Identify the iliac bifurcation (usually at or just below umbilicus level)',
      'Note: measure in transverse view; longitudinal view helps confirm structure identity',
      'Document: aorta visualized YES/NO, maximum diameter measured, echogenic thrombus present YES/NO',
      '⚠️ AAA ≥ 3 cm: document and refer for formal vascular ultrasound; ≥ 5 cm or symptomatic → emergent vascular surgery consult',
    ],
    videos: [
      {
        title: 'Abdominal Aortic Aneurysm (AAA) Ultrasound',
        url: 'https://www.youtube.com/watch?v=zHV4SSUdr04',
        channel: 'POCUS 101',
        note: 'Step-by-step AAA POCUS technique with measurement demonstration',
      },
      {
        title: 'Point-of-Care Ultrasound (POCUS) for the Generalist',
        url: 'https://www.youtube.com/watch?v=AS0CXlR-pqE',
        channel: 'Southern Medical Association',
        note: 'Broad overview of POCUS applications including aorta for generalist physicians',
      },
    ],
    butterflyLinks: [
      { label: 'Butterfly Network — Aorta POCUS Module 1', url: 'https://cloud.butterflynetwork.com/-/edu/7OVDESsuTb6ImY9YdiAakQ?utm_source=edu_share_modal' },
      { label: 'Butterfly Network — Aorta POCUS Module 2', url: 'https://cloud.butterflynetwork.com/-/edu/H-DRs0TrRS-Xy9PrkCDe_g?utm_source=edu_share_modal' },
      { label: 'Butterfly Network — Aorta POCUS Module 3', url: 'https://cloud.butterflynetwork.com/-/edu/QejBx52vQiiKcTvwZsE2kA?utm_source=edu_share_modal' },
      { label: 'Butterfly Network — Aorta POCUS Module 4', url: 'https://cloud.butterflynetwork.com/-/edu/FKhLxjquRKeVNWY_ooU6tA?utm_source=edu_share_modal' },
    ] as { label: string; url: string }[],
    aftercare: [
      'Clean probe thoroughly with approved ultrasound wipes after every patient exam',
      'Document findings in note: "Bedside POCUS performed. Aorta visualized. Maximum AP diameter [X] cm. No evidence of AAA." or equivalent',
      'If incidental AAA found (< 5 cm, asymptomatic): refer for formal vascular ultrasound; arrange vascular surgery follow-up per size guidelines',
      'If patient stable with AAA ≥ 5 cm or symptomatic: emergent surgical consultation — do not discharge',
      'Patient education: explain findings in plain terms; avoid the word "rupture" unless already ruptured',
    ],
    pearls: [
      '🎯 Key Measurement Pearl: Always measure outer wall to outer wall in TRANSVERSE — do not measure inner lumen only. Including thrombus is essential.',
      '🔍 Distinguishing Aorta vs. IVC: Aorta = LEFT of spine, pulsatile, thick walls, circular; IVC = RIGHT of spine, compressible, respiratory variation, oval.',
      '⚠️ Bowel Gas Troubleshooting: Apply slow, firm, steady pressure — push bowel gas out of the way. Try different angulation. If still cannot visualize: order CT abdomen/pelvis with contrast.',
      '📏 Diameter Threshold Quick Reference: < 3 cm = normal. 3–3.9 cm = small AAA (re-image in 3 years). 4–4.9 cm = moderate AAA (re-image in 12 months + vascular referral). ≥ 5 cm = large AAA (urgent vascular surgery referral ± repair).',
      '🚨 Do Not Miss: If patient presents with acute back/flank pain + hypotension + pulsatile mass → activated AAA until proven otherwise. Scan fast, consult fast.',
      '💡 Resident Pitfall — False Security: A "normal" POCUS does NOT rule out retroperitoneal hematoma from rupture. A ruptured AAA may decompress blood into retroperitoneum and still show a contained, measurable aorta.',
      '🦋 Butterfly Network Modules (Requires Butterfly Login): These links open interactive image interpretation modules in your Butterfly Cloud account. Highly recommended for hands-on pattern recognition practice.',
    ],
  },
  {
    id: 'pocus-dvt',
    title: 'Lower Extremity DVT POCUS',
    category: 'POCUS',
    categoryId: 'pocus',
    overview: {
      description:
        'Two-point or three-point compression ultrasound of the proximal lower extremity veins to detect deep vein thrombosis (DVT). A validated, guideline-supported bedside technique for primary care and emergency settings. Evaluates the common femoral vein (CFV) at the groin and the popliteal vein behind the knee. Normal veins fully collapse with probe compression; non-compressibility = DVT until proven otherwise.',
      indications: [
        'Unilateral leg swelling, pain, warmth, or erythema',
        'Suspected DVT in moderate-to-high pre-test probability (Wells score ≥ 2)',
        'Evaluation of calf swelling extending above the knee',
        'Follow-up imaging after anticoagulation initiation',
        'Unexplained hypoxia or tachycardia with concurrent leg symptoms',
        'Pregnant patients with unilateral leg swelling',
      ],
      contraindications: [
        'No absolute contraindications to compression ultrasound',
        'Open wound, severe cellulitis, or skin breakdown over compression site: use clinical judgment; probe may still be used with sterile gel/cover',
        'Known femoral or popliteal artery aneurysm: extreme care with compression',
        'This is a limited exam — does NOT evaluate calf veins or iliac veins; negative proximal POCUS does not fully exclude PE',
      ],
    },
    supplies: [
      'Ultrasound machine with linear high-frequency probe (7–15 MHz) for vascular',
      'Ultrasound gel',
      'Sterile probe cover if over skin break or wound',
      'Measuring calipers (most US machines have built-in electronic calipers)',
    ],
    setup: [
      'Patient position: GROIN — supine, leg externally rotated and slightly flexed at hip/knee ("frog-leg" position)',
      'Patient position: POPLITEAL — prone or lateral decubitus with knee slightly flexed; alternatively supine with knee raised',
      'Apply gel liberally to femoral triangle region and behind knee',
      'Select linear probe (7–15 MHz); vascular or venous preset; depth 3–5 cm',
      'Explain procedure: patient will feel firm but not painful pressure from probe',
      'Color Doppler available for supplemental confirmation — not required for compression technique',
    ],
    steps: [
      'GROIN POINT: Identify the common femoral vein (CFV) in transverse view — medial to common femoral artery (CFA); CFA pulsates, CFV does NOT',
      'Apply gentle downward compression to CFV — normal vein walls fully collapse (touch each other)',
      'NON-COMPRESSIBLE vein = positive for DVT — walls fail to meet with pressure',
      'Trace the CFV distally to the saphenofemoral junction (SFJ); compress at junction separately',
      'Continue to femoral vein (FV) in the thigh — compress in multiple positions along its course',
      'POPLITEAL POINT: Place probe behind the knee; identify popliteal vein (pulsating artery nearby); compress popliteal vein',
      'Compress at multiple levels behind knee — include bifurcation into tibial veins if visible',
      'Color Doppler: confirm venous flow (blue/red) and respiratory phasicity in CFV',
      '⚠️ If non-compressible segment found: document location, measure vein diameter, note presence of echogenic thrombus; immediately refer/consult',
    ],
    videos: [
      {
        title: 'Venous Ultrasound: Executing a DVT Ultrasound Study',
        url: 'https://www.youtube.com/watch?v=QY3RJrDGX8E',
        channel: 'Medmastery',
        note: 'Step-by-step technique for performing compression DVT ultrasound',
      },
      {
        title: 'Venous Ultrasound: Interpreting DVT Compressibility',
        url: 'https://www.youtube.com/watch?v=vek9zkU2rX0',
        channel: 'Medmastery',
        note: 'How to correctly interpret compressibility findings and chronic vs acute thrombus',
      },
    ],
    butterflyLinks: [
      { label: 'Butterfly Network — DVT POCUS Module 1', url: 'https://cloud.butterflynetwork.com/-/edu/Xh6EttZQTx-smkevjPdFrA?utm_source=edu_share_modal' },
      { label: 'Butterfly Network — DVT POCUS Module 2', url: 'https://cloud.butterflynetwork.com/-/edu/7sdrhZY-SXarp1ZSmga8kg?utm_source=edu_share_modal' },
      { label: 'Butterfly Network — DVT POCUS Module 3', url: 'https://cloud.butterflynetwork.com/-/edu/aNwXNhFbSHqo0QOjcYXD-Q?utm_source=edu_share_modal' },
      { label: 'Butterfly Network — DVT POCUS Module 4', url: 'https://cloud.butterflynetwork.com/-/edu/EltADLAOQemQSJy_xxPH4Q?utm_source=edu_share_modal' },
    ] as { label: string; url: string }[],
    aftercare: [
      'Document findings: veins evaluated, compressibility at each point, Doppler phasicity, any non-compressible segments',
      'Positive DVT: initiate anticoagulation per institutional protocol (DOACs first-line per guidelines); exclude contraindications',
      'Negative POCUS with high clinical suspicion: consider formal vascular lab ultrasound same day; do not discharge on low-probability alone if Wells score ≥ 2',
      'Educate patient: activity restrictions, signs of PE (dyspnea, pleuritic chest pain, hemoptysis), follow-up plan',
      'Bilateral DVT: consider underlying malignancy, hypercoagulable state, or IVC thrombus — broaden workup',
    ],
    pearls: [
      '🎯 Core Skill: Compression is the test. Forget Doppler for initial screen. Non-compressible = DVT. Fully compressible with no echogenic clot = no proximal DVT.',
      '🔍 Artery vs. Vein: Artery pulsates and does NOT compress. Vein is larger, non-pulsatile, and compresses easily. Use color Doppler to confirm if uncertain.',
      '⚠️ Pitfall — Partial Compression: Make sure you compress in true transverse, not oblique. Oblique compression gives false appearance of partial compressibility.',
      '💡 Chronic vs. Acute DVT: Acute thrombus = echogenic, heterogeneous, vein may be distended. Chronic = densely echogenic/calcified, vein wall thickened. Clinical correlation required; formal vascular lab helps differentiate.',
      '📋 Wells Score Reminder: Score ≥ 2 = high probability. Active cancer (+1), immobilization (+1), bedridden > 3d (+1), entire leg swollen (+1), calf swelling > 3 cm (+1), pitting edema (+1), collateral superficial veins (+1), prior DVT (+1), alternative diagnosis equally likely (−2).',
      '🚨 Do Not Miss: Negative proximal POCUS does NOT rule out isolated calf DVT or PE. If clinical suspicion for PE is high despite negative POCUS → pursue V/Q or CTPA.',
      '🦋 Butterfly Network Modules (Requires Butterfly Login): Includes compression technique walkthroughs and image interpretation exercises using Butterfly IQ probe.',
    ],
  },
  {
    id: 'pocus-ob1',
    title: 'Limited OB Ultrasound — 1st Trimester',
    category: 'POCUS',
    categoryId: 'pocus',
    overview: {
      description:
        'Limited bedside obstetric ultrasound to confirm intrauterine pregnancy (IUP), assess viability, estimate gestational age, and rule out ectopic pregnancy in the first trimester (≤ 13 6/7 weeks). A critical skill for any provider seeing patients with early pregnancy concerns. The primary goal in most acute settings is confirming IUP vs. ruling out ectopic — not detailed fetal survey.',
      indications: [
        'Confirm intrauterine pregnancy in patient with positive urine hCG',
        'Evaluate for ectopic pregnancy — pelvic pain, vaginal bleeding, or adnexal tenderness in early pregnancy',
        'Assess fetal viability (cardiac activity) in threatened abortion',
        'Estimate gestational age by crown-rump length (CRL) — accurate to ± 5–7 days in first trimester',
        'Evaluate for subchorionic hematoma',
        'Assess cervical length in patients with history of preterm cervical changes (limited)',
      ],
      contraindications: [
        'No absolute contraindications to obstetric ultrasound',
        'Transabdominal ultrasound may be limited by body habitus or early gestational age — transvaginal ultrasound often required before 8–10 weeks',
        'Transvaginal ultrasound: patient refusal is a relative contraindication; defer to formal radiology US',
        'This is a LIMITED exam — does not replace formal fetal anatomic survey; refer to OB for complete evaluation',
      ],
    },
    supplies: [
      'Ultrasound machine with curvilinear (2–5 MHz, transabdominal) and/or endovaginal probe (5–9 MHz)',
      'Ultrasound gel (warm if possible for patient comfort)',
      'Probe cover + sterile gel for transvaginal (TV) probe',
      'Sheet for draping; stirrups or positioning wedge for TV approach',
      'Bladder protocol: full bladder for transabdominal; empty bladder for transvaginal',
    ],
    setup: [
      'TRANSABDOMINAL: Patient supine with full bladder; expose lower abdomen; curvilinear probe in sagittal then transverse over midline suprapubic region',
      'TRANSVAGINAL: Patient empties bladder; lithotomy position; sterile probe cover with sterile gel on probe tip; introduce gently',
      'Preset: OB first trimester or general GYN/pelvic',
      'Depth: start at 12–15 cm (transabdominal) or 6–8 cm (transvaginal)',
      'Inform patient: explain what you are looking for and what you will do step-by-step; document informed consent for TV approach',
    ],
    steps: [
      'Identify the uterus — midline pelvic structure; follow long axis in sagittal view',
      'Locate gestational sac (GS): round/oval, echogenic ring within endometrial cavity; confirm INTRAUTERINE location',
      'Identify yolk sac within GS — first definitive sign of IUP (seen by 5–5.5 weeks TV)',
      'Identify fetal pole (embryo) adjacent to yolk sac; measure crown-rump length (CRL) from head to rump in neutral position',
      'Confirm cardiac activity: M-mode through the fetal heart; document fetal heart rate (normal: 110–170 bpm)',
      'Assess adnexa bilaterally — empty adnexa and no free fluid = reassuring; adnexal mass + no IUP = ectopic until proven otherwise',
      'Assess for free fluid in the cul-de-sac (posterior to uterus): small amount normal; large or echogenic free fluid + no IUP = emergent ectopic evaluation',
      'Measure GS mean sac diameter (MSD) if embryo not yet visible: MSD > 25 mm without embryo = anembryonic pregnancy',
      'Document all findings: IUP confirmed YES/NO, CRL, GA by CRL, FHR, adnexal findings, free fluid',
    ],
    videos: [
      {
        title: 'First Trimester Pregnancy / Obstetric Ultrasound using POCUS',
        url: 'https://www.youtube.com/watch?v=NIrHS4EZD7E',
        channel: 'POCUS 101',
        note: 'Comprehensive walkthrough of 1st trimester POCUS technique including ectopic evaluation',
      },
      {
        title: '1st Trimester Scan — Clarius Mobile Health',
        url: 'https://www.youtube.com/watch?v=4JTq2gyW5Nw',
        channel: 'Clarius Mobile Health',
        note: 'Hands-on scanning demonstration of first trimester ultrasound technique',
      },
    ],
    butterflyLinks: [
      { label: 'Butterfly Network — OB 1st Trimester Module 1', url: 'https://cloud.butterflynetwork.com/-/edu/bc8aoQjtT2GTXeletIWS2g?utm_source=edu_share_modal' },
      { label: 'Butterfly Network — OB 1st Trimester Module 2', url: 'https://cloud.butterflynetwork.com/-/edu/1zgEYlVuSUmRYTHnySFaWA?utm_source=edu_share_modal' },
      { label: 'Butterfly Network — OB 1st Trimester Module 3', url: 'https://cloud.butterflynetwork.com/-/edu/LFTbc9HyTK2bxTcM-19Xgw?utm_source=edu_share_modal' },
      { label: 'Butterfly Network — OB 1st Trimester Module 4', url: 'https://cloud.butterflynetwork.com/-/edu/M7JKYJRHTc6UIxlg1C890Q?utm_source=edu_share_modal' },
    ] as { label: string; url: string }[],
    aftercare: [
      'Document gestational age, viability, IUP vs. ectopic status clearly in chart',
      'If IUP confirmed viable: schedule 1st trimester OB panel and formal dating US if not already done',
      'If pregnancy of unknown location (no IUP, no ectopic on POCUS): correlate with hCG level; serial hCG and repeat POCUS in 48 hours; gynecology consultation',
      'Ectopic suspected (adnexal mass, free fluid, no IUP): emergent gynecology consult — do not discharge',
      'Miscarriage diagnosis (empty GS or no FHR): compassionate counseling; do not make diagnosis on a single limited scan — repeat or formal US for confirmation',
    ],
    pearls: [
      '🎯 Most Important Question: Is this an IUP? Answering this question changes everything. Do not look for fetal anatomy, gender, or placenta — that\'s for formal OB US.',
      '⚠️ Ectopic Rule: An adnexal mass + pelvic free fluid + no IUP = ectopic until proven otherwise. Do NOT discharge this patient without gynecology evaluation.',
      '🔍 Pseudogestational Sac Pitfall: An ectopic can produce fluid in the endometrial cavity that looks like a GS (pseudosac). A true GS has a double decidual reaction (two echogenic rings). A pseudosac has ONE ring. Look for yolk sac to confirm.',
      '📏 GA by CRL — Cheat Sheet: CRL 3 mm = ~6 weeks. CRL 10 mm = ~7 weeks. CRL 25 mm = ~9 weeks. CRL 60 mm = ~12 weeks.',
      '💡 Cardiac Activity Threshold: No cardiac activity with CRL ≥ 7 mm = non-viable pregnancy. Do NOT diagnose on a single scan alone — confirm with formal ultrasound within 7–14 days or gynecology consultation.',
      '🦋 Butterfly Network Modules (Requires Butterfly Login): Includes image sets for IUP confirmation, ectopic patterns, and yolk sac identification using the Butterfly IQ.',
    ],
  },
  {
    id: 'pocus-ob23',
    title: 'Limited OB Ultrasound — 2nd & 3rd Trimester',
    category: 'POCUS',
    categoryId: 'pocus',
    overview: {
      description:
        'Limited bedside transabdominal ultrasound in the second and third trimester to answer specific clinical questions: fetal viability, fetal presentation, estimated fetal weight (via biometry), amniotic fluid index (AFI), and placental location. Not a replacement for formal anatomic survey — focuses on clinically urgent, point-of-care questions during prenatal visits or acute presentations.',
      indications: [
        'Confirm fetal cardiac activity and viability at prenatal visit',
        'Determine fetal presentation (vertex vs. breech vs. transverse) in late 3rd trimester',
        'Estimate amniotic fluid — polyhydramnios or oligohydramnios',
        'Assess placental location (low-lying, previa) in 3rd trimester bleeding',
        'Measure fundal height correlation with gestational age discrepancy',
        'Biometric measurements for estimated gestational age when dating uncertain',
      ],
      contraindications: [
        'No absolute contraindications to obstetric ultrasound',
        'Limited POCUS cannot replace formal anatomic survey or growth scan',
        'Suspected fetal anomaly, IUGR evaluation, or abnormal Doppler studies require formal perinatology referral',
        'Anterior placenta previa near cervix: use POCUS to identify, but formal transvaginal US by OB required for definitive assessment',
      ],
    },
    supplies: [
      'Ultrasound machine with curvilinear probe (2–5 MHz) — transabdominal approach used exclusively in 2nd/3rd trimester',
      'Ultrasound gel (warm preferred)',
      'Fetal Doppler or M-mode for FHR documentation',
      'Electronic calipers (on-machine)',
    ],
    setup: [
      'Patient supine; elevate right hip slightly with towel to avoid aortocaval compression in 3rd trimester',
      'Expose abdomen from pubic symphysis to fundus',
      'Apply generous ultrasound gel',
      'Select curvilinear probe; OB preset; depth 12–20 cm depending on gestational age',
      'Orient screen: transabdominal scans display fetal head to viewer\'s LEFT by convention',
      'Ask patient about prior placenta previa or c-section scar before applying probe (adjust approach accordingly)',
    ],
    steps: [
      'ORIENTATION: Identify fetal lie (longitudinal vs. transverse vs. oblique) in sagittal scan',
      'PRESENTATION: Determine presenting part at the pelvis — head (vertex) = round, echogenic skull with midline echo; Breech = softer, round fetal body without skull features',
      'CARDIAC ACTIVITY: Locate fetal chest; use M-mode through the heart; document FHR in bpm',
      'BIOMETRY (if indicated): Measure BPD (biparietal diameter) at level of thalami; HC (head circumference) at same level; AC (abdominal circumference) at level of portal vein and stomach; FL (femur length)',
      'AMNIOTIC FLUID INDEX (AFI): Divide uterus into 4 quadrants; measure deepest vertical pocket in each without umbilical cord; sum = AFI. Normal: 8–24 cm. < 5 = oligohydramnios; > 24 = polyhydramnios',
      'PLACENTAL LOCATION: Identify echogenic placenta — anterior, posterior, fundal, or low-lying; document relationship to internal cervical os (if low-lying)',
      'FETAL WEIGHT ESTIMATE: Machine calculates EFW using BPD + HC + AC + FL with standard formula (Hadlock)',
      'Document: presentation, FHR, placental location, AFI, biometric measurements with EFW if obtained',
    ],
    videos: [
      {
        title: '2nd Trimester OB Scan',
        url: 'https://www.youtube.com/watch?v=2MO3en1D_w8',
        channel: 'Sonographic Tendencies',
        note: 'Full 2nd trimester scan walkthrough including biometry technique',
      },
      {
        title: 'Pregnancy BPD HC AC and FL Measurements',
        url: 'https://www.youtube.com/watch?v=WKnFD6KeO4c',
        channel: 'Sonosite',
        note: 'How to correctly obtain and measure all four standard obstetric biometric parameters',
      },
    ],
    butterflyLinks: [
      { label: 'Butterfly Network — OB 2nd/3rd Trimester Module 1', url: 'https://cloud.butterflynetwork.com/-/edu/5I2l-sDWR7-1ue3uwRDdNQ?utm_source=edu_share_modal' },
      { label: 'Butterfly Network — OB 2nd/3rd Trimester Module 2', url: 'https://cloud.butterflynetwork.com/-/edu/G9adXqDLTryrI1nSleI4OQ?utm_source=edu_share_modal' },
      { label: 'Butterfly Network — OB 2nd/3rd Trimester Module 3', url: 'https://cloud.butterflynetwork.com/-/edu/WVljvehsSXKHdgmH9DVUpA?utm_source=edu_share_modal' },
    ] as { label: string; url: string }[],
    aftercare: [
      'Document all measurements, gestational age by US, EFW percentile, presentation, FHR, AFI, and placental location in chart',
      'Compare US-derived GA with LMP-based GA — if discrepancy > 2 weeks, consider formal dating ultrasound or perinatology referral',
      'Breech presentation at ≥ 36 weeks: refer for OB consult; discuss external cephalic version (ECV) options',
      'Oligohydramnios (AFI < 5): same-day OB consultation; do not discharge without evaluation',
      'Placenta previa suspected: do NOT perform digital cervical exam; refer for formal TV ultrasound by OB',
    ],
    pearls: [
      '🎯 Most Useful Limited Questions in 3rd Trimester: (1) Vertex or breech? (2) Cardiac activity present? (3) Where is the placenta? These three answers guide almost every 3rd-trimester clinical decision at a basic level.',
      '📏 Biometry Order Pearl: BPD → HC → AC → FL. Measure in this sequence every time — it matches head-to-toe and reduces steps. Don\'t skip AC — it\'s the most sensitive parameter for growth restriction.',
      '⚠️ AFI Pitfall: Measure the DEEPEST vertical pocket free of cord. Measuring through cord artificially inflates AFI. If unsure, use Color Doppler to confirm the pocket is cord-free.',
      '🔍 Placenta Previa Warning Sign: If the placenta appears to reach or overlap the internal os — stop exam; document; do NOT perform digital cervical exam. Refer immediately.',
      '💡 Presentation Assessment Tip: Vertex = round, hard, echogenic skull dome. Breech = soft, rounder, spinal column extending into pelvis. If uncertain: feel the fundus for the harder head.',
      '🦋 Butterfly Network Modules (Requires Butterfly Login): Covers biometry technique, AFI measurement, and placenta evaluation using Butterfly IQ for office-based OB care.',
    ],
  },
  {
    id: 'pocus-soft-tissue',
    title: 'Soft Tissue & MSK POCUS',
    category: 'POCUS',
    categoryId: 'pocus',
    overview: {
      description:
        'High-frequency ultrasound to evaluate soft tissue structures including suspected abscess vs. cellulitis, foreign body localization, evaluation of superficial cysts, and musculoskeletal structures (tendons, ligaments, bursae, joints). Particularly high-yield in primary care for distinguishing abscess (requiring I&D) from cellulitis (requiring antibiotics only) — dramatically changes management.',
      indications: [
        'Suspected skin/soft tissue abscess — distinguish from cellulitis; guide I&D',
        'Foreign body localization (wood, plastic, metal, glass in superficial tissue)',
        'Evaluation of lipoma, sebaceous cyst, ganglion cyst',
        'Tendon evaluation: suspected rupture (Achilles, bicep, quadriceps), tendinopathy',
        'Bursal pathology: prepatellar, olecranon bursitis — fluid vs. inflammation',
        'Joint effusion identification for aspiration planning (wrist, ankle, small joints)',
        'Muscle or soft tissue hematoma delineation',
      ],
      contraindications: [
        'No absolute contraindications to diagnostic soft tissue ultrasound',
        'Active cellulitis with extreme tenderness — probe still usable but patient may need pain management first',
        'Deep structures (> 6 cm) beyond high-frequency probe range — CT/MRI required',
        'Suspected osteomyelitis requires MRI for definitive evaluation; ultrasound may show periosteal reaction only',
      ],
    },
    supplies: [
      'Ultrasound machine with high-frequency LINEAR probe (7–15 MHz) — essential for soft tissue work',
      'Ultrasound gel; consider standoff pad or generous gel mound for very superficial structures',
      'Sterile probe cover if performing ultrasound-guided drainage',
      'For I&D: standard abscess kit (scalpel, hemostat, packing material, irrigation syringe)',
    ],
    setup: [
      'Patient position: comfortable; area of interest fully exposed and accessible',
      'High-frequency linear probe selected; vascular or superficial soft tissue preset; depth 3–5 cm',
      'Apply generous ultrasound gel — a thick gel pad improves contact for superficial structures',
      'Scan in TWO perpendicular planes (longitudinal AND transverse) for all findings',
      'Compare to contralateral side when assessing tendons or bursae (often dramatically helpful)',
      'Use light probe pressure — excess pressure can collapse a small abscess cavity',
    ],
    steps: [
      'CELLULITIS vs. ABSCESS: Place probe over erythematous, tender area; scan systematically',
      'Cellulitis pattern: diffuse hyperechoic cobblestoning of subcutaneous fat; no discrete fluid pocket; no anechoic/hypoechoic collection',
      'Abscess pattern: discrete anechoic or hypoechoic fluid collection with posterior acoustic enhancement; may have internal echoes, septations, or surrounding hyperemia on Doppler',
      'Map abscess: measure size in 3 planes; identify depth from skin to collection; note proximity to vessels, nerves, fascia',
      'FOREIGN BODY: Look for hyperechoic focus with acoustic shadowing (metal, glass) or hyperechoic focus without shadow (wood, plastic); scan in two planes; mark skin overlying location',
      'TENDON: Scan in longitudinal plane; tendons appear as fibrillar hyperechoic structures; rupture = hypoechoic gap or discontinuity in fibers',
      'BURSA: Oval anechoic sac between structures; measure diameter; assess for internal echoes (infection) or internal vascularity on Doppler',
      'Document: structure examined, findings, size/depth of collection, plan (I&D, aspiration, referral)',
    ],
    videos: [
      {
        title: 'POCUS for Skin and Soft Tissue Evaluation — Infections, Cysts & Ruptures',
        url: 'https://www.youtube.com/watch?v=w8wc8-40F1E',
        channel: 'POCUS for Primary Care',
        note: 'Practical soft tissue POCUS covering abscess, cyst, and infection patterns',
      },
      {
        title: 'Soft Tissue Ultrasound — Case-Based Review',
        url: 'https://www.youtube.com/watch?v=Kp2kqn43YKg',
        channel: 'Joseph Minardi',
        note: 'Case-based teaching of soft tissue and MSK ultrasound findings',
      },
    ],
    butterflyLinks: [
      { label: 'Butterfly Network — Soft Tissue POCUS Module 1', url: 'https://cloud.butterflynetwork.com/-/edu/B30sCc6eTv6ZguLFyrh8hQ?utm_source=edu_share_modal' },
      { label: 'Butterfly Network — Soft Tissue POCUS Module 2', url: 'https://cloud.butterflynetwork.com/-/edu/8s3m4MltSj2UJ6l6mL6GTg?utm_source=edu_share_modal' },
      { label: 'Butterfly Network — Soft Tissue POCUS Module 3', url: 'https://cloud.butterflynetwork.com/-/edu/M6k4DE3sTLiLyqjpx9SB4g?utm_source=edu_share_modal' },
    ] as { label: string; url: string }[],
    aftercare: [
      'Abscess confirmed → proceed with I&D under real-time ultrasound guidance or after marking; pack wound; culture if immunocompromised or recurrent',
      'Cellulitis only confirmed → antibiotics per clinical presentation; no drainage; re-evaluate in 48–72 hours',
      'Foreign body localized → mark skin, arrange removal in appropriate setting; document depth and proximity to structures',
      'Tendon rupture confirmed → immobilize; orthopedic referral; formal MRI if surgical planning required',
      'Post-I&D: consider post-procedure scan to confirm pocket adequately drained; document residual fluid',
    ],
    pearls: [
      '🎯 Game-Changing Use Case: Abscess vs. cellulitis is arguably the highest-yield soft tissue POCUS skill in primary care. Physical exam alone misclassifies 15–20% of cases. A 30-second scan changes management.',
      '🔍 Squish Test: Apply gentle probe compression. Abscess fluid "swirls" on real-time imaging (positive squish sign). Solid masses do not swirl. Useful for differentiation.',
      '⚠️ Light Pressure Required: Firm pressure can collapse a small or superficial abscess — always use LIGHT probe contact. Add more gel rather than more pressure to improve contact.',
      '💡 Posterior Acoustic Enhancement: Fluid-filled structures enhance the signal below them (brighter zone posterior to collection). This feature helps confirm true fluid — look for it every time you suspect abscess.',
      '📏 Depth Matters for I&D: If an abscess is > 4 cm deep from skin, surgical or procedural referral is generally safer — standard I&D can be inadequate and risks neurovascular injury.',
      '🦋 Butterfly Network Modules (Requires Butterfly Login): Interactive image interpretation cases for abscess, cellulitis, cyst, tendon, and foreign body patterns using the Butterfly IQ.',
    ],
  },
  {
    id: 'pocus-gallbladder',
    title: 'Limited GI / Gallbladder POCUS',
    category: 'POCUS',
    categoryId: 'pocus',
    overview: {
      description:
        'Focused right upper quadrant (RUQ) ultrasound to evaluate the gallbladder and biliary system for cholelithiasis, acute cholecystitis, and biliary dilation. The gallbladder is one of the most accessible abdominal structures with ultrasound and provides high-yield diagnostic information in patients presenting with RUQ pain, nausea, or postprandial discomfort. Sensitivity for gallstones approaches 94–98% with proper technique.',
      indications: [
        'Right upper quadrant or epigastric pain — evaluate for cholelithiasis',
        'Suspected acute cholecystitis — gallbladder wall thickening, pericholecystic fluid, sonographic Murphy\'s sign',
        'Nausea, vomiting, and fatty food intolerance with RUQ tenderness',
        'Jaundice evaluation — assess for biliary dilation (CBD > 6 mm is suspicious)',
        'Unexplained elevated liver enzymes or bilirubin',
        'Elevated alkaline phosphatase with RUQ symptoms',
      ],
      contraindications: [
        'No absolute contraindications to diagnostic ultrasound of the biliary system',
        'Bowel gas, obesity, or prior abdominal surgery may limit visualization — formal radiology US required if POCUS inadequate',
        'This is a LIMITED exam — pancreas, right kidney, hepatic parenchyma require full formal US; refer if broader pathology suspected',
        'POCUS cannot reliably evaluate CBD stones (choledocholithiasis) — formal US or MRCP required',
      ],
    },
    supplies: [
      'Ultrasound machine with curvilinear probe (2–5 MHz) for deep abdominal imaging',
      'Ultrasound gel',
      'Patient ideally fasting ≥ 4–6 hours (gallbladder maximally distended when not contracting post-meal)',
    ],
    setup: [
      'Patient position: SUPINE first; then LEFT LATERAL DECUBITUS (LLD) for best gallbladder view — stones will shift with gravity',
      'Fasting state preferred: contracted gallbladder after eating is much harder to evaluate',
      'Apply gel to RUQ; intercostal approach often required — probe placed in intercostal spaces to avoid rib shadow',
      'Curvilinear probe; abdominal preset; depth 12–16 cm initially',
      'Intercostal windows: place probe between ribs, angled toward liver; ask patient to INHALE and HOLD — brings liver/gallbladder into view',
      'Subcostal approach: slide probe below costal margin angled superiorly — alternative view',
    ],
    steps: [
      'FIND THE LIVER: Start with large target — identify liver parenchyma in RUQ',
      'FOLLOW THE PORTAL VEIN: The gallbladder lies inferior and anterior to the right portal vein — follow it to the "exclamation point" sign (portal vein + gallbladder together)',
      'IDENTIFY THE GALLBLADDER: Ovoid, anechoic (black) structure with posterior acoustic enhancement; echogenic wall',
      'SCAN ENTIRE GALLBLADDER: Sweep from fundus to neck in BOTH longitudinal and transverse planes',
      'ASSESS FOR STONES: Hyperechoic foci with POSTERIOR ACOUSTIC SHADOWING that move with gravity (shift in LLD position) = gallstones',
      'ASSESS GB WALL: Normal < 3 mm; wall > 3 mm = abnormal; > 5 mm with symptoms = cholecystitis until proven otherwise',
      'PERICHOLECYSTIC FLUID: Anechoic rim around the gallbladder = significant finding; suggests acute cholecystitis',
      'SONOGRAPHIC MURPHY\'S SIGN: Maximal tenderness with direct probe pressure over the gallbladder — highly specific for acute cholecystitis',
      'COMMON BILE DUCT (CBD): Identify CBD adjacent to portal vein (anterior); measure anteroposterior diameter; > 6 mm without prior cholecystectomy = dilated',
      'Document: gallbladder size, wall thickness, stones (number, size, mobility), pericholecystic fluid, sonographic Murphy, CBD diameter',
    ],
    videos: [
      {
        title: 'POCUS — How to Find the Gallbladder',
        url: 'https://www.youtube.com/watch?v=FdlX-ypMnbE',
        channel: 'Gulfcoast Ultrasound Institute',
        note: 'Practical technique for locating and scanning the gallbladder with POCUS',
      },
      {
        title: 'Point of Care Ultrasound of the Gallbladder — AMBOSS',
        url: 'https://www.youtube.com/watch?v=S4OVdaXddPk',
        channel: 'AMBOSS: Medical Knowledge Distilled',
        note: 'Gallbladder POCUS pathology: stones, cholecystitis, biliary dilation',
      },
    ],
    butterflyLinks: [
      { label: 'Butterfly Network — Gallbladder POCUS Module 1', url: 'https://cloud.butterflynetwork.com/-/edu/aXhNoOj6Q3qJELEdQYGYjA?utm_source=edu_share_modal' },
      { label: 'Butterfly Network — Gallbladder POCUS Module 2', url: 'https://cloud.butterflynetwork.com/-/edu/eloENTSWSiGQ_rLHFrY_JQ?utm_source=edu_share_modal' },
      { label: 'Butterfly Network — Gallbladder POCUS Module 3', url: 'https://cloud.butterflynetwork.com/-/edu/ho4y4EwLSwiSbxQLwlZEig?utm_source=edu_share_modal' },
    ] as { label: string; url: string }[],
    aftercare: [
      'Cholelithiasis without cholecystitis: counsel patient on low-fat diet; refer to general surgery for elective cholecystectomy discussion; repeat ultrasound if symptoms progress',
      'Acute cholecystitis (wall > 3–5 mm, pericholecystic fluid, positive sonographic Murphy): emergent general surgery consultation; NPO; IV access; analgesia; do not discharge',
      'CBD dilation (> 6 mm): order formal RUQ ultrasound and LFTs/bilirubin panel; consider MRCP; hepatobiliary or GI referral',
      'Inadequate visualization: document limitation; order formal radiology RUQ ultrasound same day if acute cholecystitis suspected',
      'Document clearly: "Bedside POCUS performed. Gallbladder visualized/not visualized. Findings: [...]"',
    ],
    pearls: [
      '🎯 Intercostal Window Trick: If the gallbladder is hidden under ribs — use intercostal spaces! Place probe between ribs and angle toward liver; ask patient to breathe in and hold. This drops the liver/GB into view.',
      '🔍 Gallstone vs. Polyp: Stones SHADOW and MOVE with gravity (reposition patient). Polyps do NOT move and do NOT shadow. When in doubt, rock the patient side to side and watch.',
      '⚠️ Sonographic Murphy\'s Sign: Press probe directly into maximal tenderness location over gallbladder while watching screen — patient abruptly stops inspiration when GB directly compressed = positive. High specificity for acute cholecystitis.',
      '💡 Sludge vs. Stone: Sludge = low-level echoes that layer in GB fundus without shadow; no movement with quick position change. Stones = shadow + gravity-dependent shift.',
      '📏 GB Wall Measurement Pearl: Measure the ANTERIOR wall only (closest to probe) — posterior wall measurement is less reliable due to posterior acoustic enhancement. Anterior wall > 3 mm = abnormal.',
      '🔧 Cannot Find the Gallbladder? Try: (1) Ask patient to roll LEFT — GB moves toward probe. (2) Try intercostal approach. (3) Follow the portal vein. (4) Ask about prior cholecystectomy.',
      '🦋 Butterfly Network Modules (Requires Butterfly Login): Covers gallstone patterns, cholecystitis features, and CBD measurement technique using the Butterfly IQ probe.',
    ],
  },
];

export const getProcedureById = (id: string): ProcedureData | undefined =>
  procedures.find((p) => p.id === id);

export const getProceduresByCategory = (categoryId: string): ProcedureData[] =>
  procedures.filter((p) => p.categoryId === categoryId);
